import React, { useEffect, useState, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useFirebase } from '@/contexts/FirebaseContext';
import { collection, query, where, getDocs, getFirestore, doc, getDoc, setDoc, updateDoc, addDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { format, parseISO, addDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { User, Calendar, Clock, BookOpen, MapPin, Info, BarChart, List, ChevronRight, ChevronDown, ChevronUp, Loader2, CheckCircle, FileText, Download, ChevronLeft, Users } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { getAllCourses } from "@/utils/firestoreService";
import { Course as CourseType } from "@/types/courseTypes";
import { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";

// Buffer time configuration - 15 minutes between lessons for instructor break
const LESSON_BUFFER_MINUTES = 15;

interface Lesson {
  id: string;
  instructorId: string;
  studentId: string;
  studentName?: string;
  studentEmail?: string;
  courseName?: string;
  type?: string;
  date?: string;
  startDate?: string;
  time?: string;
  startTime?: string;
  status: string;
  duration?: number;
  venue?: string;
  enrollmentId?: string;
  courseType?: string;
  courseSubtype?: string;
  totalHours?: number;
  hoursTaken?: number;
  hoursRemaining?: number;
  vehicleType?: string;
  licenseType?: string;
}

interface Schedule {
  id: string;
  date: string;
  startTime: string;
  duration: number;
  courseName: string;
  studentName: string;
  studentEmail: string;
  courseType: string;
  courseSubtype: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  vehicleType: string;
  licenseType: string;
  status: string;
  venue: string;
  courseId?: string;
  studentId: string;
  colors: { backgroundColor: string; borderColor: string };
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  display: 'block';
  extendedProps: {
    studentName?: string;
    studentEmail?: string;
    studentPhone?: string;
    studentAddress?: string;
    gearbox?: string;
    licenseNumber?: string;
    courseName?: string;
    courseType?: string;
    courseSubtype?: string;
    totalHours?: number;
    hoursTaken?: number;
    hoursRemaining?: number;
    vehicleType?: string;
    licenseType?: string;
    scheduleId?: string;
    status?: string;
    venue?: string;
    duration?: number;
    notes?: string;
    completedLessons?: number;
    totalLessons?: number;
    lastLessonDate?: string;
    studentPostcode?: string;
    isUnavailable?: boolean;
    isFullDay?: boolean;
    reason?: string;
  };
}

interface StudentDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postcode: string;
  gearbox: string;
  licenseNumber: string;
  progress: {
    completedLessons: number;
    totalLessons: number;
    lastLessonDate: string;
  };
}

interface Course {
  id: string;
  title: string;
  type: string;
  subtype?: string;
  description?: string;
  price?: number;
  duration?: number;
  totalHours?: number;
  vehicleType?: string;
  licenseType?: string;
  published?: boolean;
  contents?: any[];
  createdAt?: string;
  updatedAt?: string;
}

interface UserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  role: string;
  phone?: string;
  address?: string;
  postcode?: string;
  gearbox?: string;
  licenseNumber?: string;
}

interface CourseData {
  id: string;
  title: string;
  type: string;
  subtype?: string;
  description?: string;
  price?: number;
  duration?: number;
  totalHours?: number;
  vehicleType?: string;
  licenseType?: string;
}

interface EnrollmentData {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseName: string;
  courseType: string;
  courseSubtype: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  vehicleType: string;
  licenseType: string;
  startTime: string;
  endTime: string;
  status: string;
  instructorId: string;
  instructorName: string;
  instructorEmail: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Student {
  id: string;
  originalId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postcode: string;
  gearbox: string;
  licenseNumber: string;
  enrollments: Enrollment[];
}

interface Enrollment {
  id: string;
  userId: string;
  courseName: string;
  courseType: string;
  courseSubtype?: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  vehicleType: string;
  licenseType: string;
}

// Function to generate a color based on a string
const getColorFromString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

// Function to get color based on course type
const getColorForCourseType = (courseType: string): { backgroundColor: string; borderColor: string } => {
  switch (courseType.toLowerCase()) {
    case 'practical':
      return {
        backgroundColor: '#4CAF50', // Green
        borderColor: '#388E3C'
      };
    case 'theory':
      return {
        backgroundColor: '#2196F3', // Blue
        borderColor: '#1976D2'
      };
    case 'combined':
      return {
        backgroundColor: '#9C27B0', // Purple
        borderColor: '#7B1FA2'
      };
    default:
      return {
        backgroundColor: '#607D8B', // Blue Grey
        borderColor: '#455A64'
      };
  }
};

// Function to generate a unique color for each course
const generateCourseColors = (courses: Course[]) => {
  const colors = [
    { backgroundColor: '#FF4B4B', borderColor: '#FF3333' }, // Bright Red
    { backgroundColor: '#00D1FF', borderColor: '#00B8E6' }, // Bright Cyan
    { backgroundColor: '#4CAF50', borderColor: '#43A047' }, // Vibrant Green
    { backgroundColor: '#FFD700', borderColor: '#FFC107' }, // Bright Yellow
    { backgroundColor: '#9C27B0', borderColor: '#7B1FA2' }, // Rich Purple
    { backgroundColor: '#FF4081', borderColor: '#F50057' }, // Hot Pink
    { backgroundColor: '#00BCD4', borderColor: '#0097A7' }, // Bright Teal
    { backgroundColor: '#FF9800', borderColor: '#F57C00' }, // Bright Orange
    { backgroundColor: '#8BC34A', borderColor: '#7CB342' }, // Light Green
    { backgroundColor: '#E91E63', borderColor: '#C2185B' }, // Deep Pink
    { backgroundColor: '#3F51B5', borderColor: '#303F9F' }, // Indigo
    { backgroundColor: '#009688', borderColor: '#00796B' }, // Teal
  ];

  const colorMap: Record<string, { backgroundColor: string; borderColor: string }> = {};
  courses.forEach((course, index) => {
    colorMap[course.title] = colors[index % colors.length];
  });

  // Add default color for unknown courses
  colorMap['default'] = { backgroundColor: '#607D8B', borderColor: '#455A64' }; // Blue Grey

  return colorMap;
};

// Update the getColorForCourse function to use consistent colors
const getColorForCourse = (courseName: string) => {
  const colors = {
    'Intensive Course': { backgroundColor: '#FF4B4B', borderColor: '#FF3333' }, // Bright Red
    'Semi-Intensive Course': { backgroundColor: '#00D1FF', borderColor: '#00B8E6' }, // Bright Cyan
    'Standard Course': { backgroundColor: '#4CAF50', borderColor: '#43A047' }, // Vibrant Green
    'Automatic Course': { backgroundColor: '#FFD700', borderColor: '#FFC107' }, // Bright Yellow
    'Manual Course': { backgroundColor: '#9C27B0', borderColor: '#7B1FA2' }, // Rich Purple
    'Theory Course': { backgroundColor: '#2196F3', borderColor: '#1976D2' }, // Blue
    'Practical Course': { backgroundColor: '#4CAF50', borderColor: '#43A047' }, // Green
    'Refresher Course': { backgroundColor: '#FF9800', borderColor: '#F57C00' }, // Orange
    'Combined': { backgroundColor: '#9C27B0', borderColor: '#7B1FA2' }, // Purple
    'Combined Driving Lesson': { backgroundColor: '#9C27B0', borderColor: '#7B1FA2' }, // Purple
    'default': { backgroundColor: '#607D8B', borderColor: '#455A64' } // Blue Grey
  };

  return colors[courseName as keyof typeof colors] || colors.default;
};

const InstructorCalendar = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [studentDetails, setStudentDetails] = useState<StudentDetails | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoadingStudentDetails, setIsLoadingStudentDetails] = useState(false);
  const [studentDetailsError, setStudentDetailsError] = useState<string | null>(null);
  const { currentUser, getUserByEmail, getFirestore } = useFirebase();
  const [courses, setCourses] = useState<Course[]>([]);
  const [unavailableSlots, setUnavailableSlots] = useState<any[]>([]);
  const [courseColors, setCourseColors] = useState<Record<string, { backgroundColor: string; borderColor: string }>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isUnavailabilityDialogOpen, setIsUnavailabilityDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isFullDay, setIsFullDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [isLoadingBooking, setIsLoadingBooking] = useState(false);
  const [selectedDateForBooking, setSelectedDateForBooking] = useState<Date | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [bookingData, setBookingData] = useState({
    date: "",
    startTime: "",
    duration: "1",
    venue: "",
    notes: "",
  });
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [availabilityError, setAvailabilityError] = useState<string>("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [unavailabilityReason, setUnavailabilityReason] = useState('');
  const [repeatSettings, setRepeatSettings] = useState<UnavailabilityRepeat>({ type: 'none' });
  const [isCancelling, setIsCancelling] = useState(false);

  // Add state for collapsible sections and weekly report
  const [isTodayLessonsCollapsed, setIsTodayLessonsCollapsed] = useState(false);
  const [isWeeklyReportOpen, setIsWeeklyReportOpen] = useState(false);
  const [weeklyReportData, setWeeklyReportData] = useState<any>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Add state for overview period selection
  const [overviewPeriod, setOverviewPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedOverviewDate, setSelectedOverviewDate] = useState<Date>(new Date());

  // Add state for report functionality
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [isLoadingLessonDetails, setIsLoadingLessonDetails] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Add a state for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Add state for past date booking
  const [allowPastDateBooking, setAllowPastDateBooking] = useState(false);

  // Add state for reschedule functionality
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    date: "",
    startTime: "",
    duration: "1",
    venue: "",
    notes: "",
  });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleAvailableTimeSlots, setRescheduleAvailableTimeSlots] = useState<string[]>([]);

  // Add useEffect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update the CSS styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Hide the FullCalendar license message */
      .fc-license-message {
        display: none !important;
      }

      /* Color calendar slots from 10 PM (22:00) to 7 AM (07:00) with dark grey */
      .dark-grey-slot {
        background-color: rgba(107, 114, 128, 0.3) !important;
      }

      .unavailable-full-day {
        background-color: #4B5563 !important;
        border-color: #374151 !important;
        color: white !important;
        opacity: 0.8 !important;
      }
      .unavailable-time-slot {
        background-color: #F5F5F5 !important;
        border-color: #E0E0E0 !important;
        color: #757575 !important;
      }
      /* Remove the general unavailable day styles */
      .fc-day.unavailable-day {
        position: relative !important;
      }
      .fc-day.unavailable-day .fc-daygrid-day-number,
      .fc-day.unavailable-day .fc-col-header-cell-cushion {
        color: inherit !important;
      }
      .fc-timegrid-col.unavailable-day {
        position: relative !important;
      }
      .fc-timegrid-col.unavailable-day .fc-timegrid-col-frame {
        background-color: transparent !important;
      }
      .fc-timegrid-col.unavailable-day .fc-timegrid-slot-label {
        color: inherit !important;
      }
      /* Styles for full day unavailability */
      .fc-day.unavailable-full-day {
        background-color: #4B5563 !important;
      }
      .fc-day.unavailable-full-day .fc-daygrid-day-frame {
        background-color: #4B5563 !important;
      }
      .fc-day.unavailable-full-day .fc-daygrid-day-number,
      .fc-day.unavailable-full-day .fc-col-header-cell-cushion {
        color: white !important;
      }
      .fc-day.unavailable-full-day .fc-daygrid-day-frame::after {
        content: 'Unavailable';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-30deg);
        font-size: 1.2em;
        color: white;
        font-weight: 600;
        z-index: 1;
        opacity: 0.5;
        white-space: nowrap;
        pointer-events: none;
      }
      .fc-timegrid-col.unavailable-full-day {
        background-color: #4B5563 !important;
      }
      .fc-timegrid-col.unavailable-full-day .fc-timegrid-col-frame {
        background-color: #4B5563 !important;
      }
      .fc-timegrid-col.unavailable-full-day .fc-timegrid-slot-label {
        color: white !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Update the isDateUnavailable function to only check for partial unavailability
  const isDateUnavailable = (date: Date) => {
    return unavailableSlots.some(slot => {
      if (slot.isFullDay) return false; // Skip full day slots
      const [year, month, day] = slot.date.split('-').map(Number);
      const slotDate = new Date(year, month - 1, day);
      return date.toDateString() === slotDate.toDateString();
    });
  };

  // Keep the isDateFullyUnavailable function as is
  const isDateFullyUnavailable = (date: Date) => {
    return unavailableSlots.some(slot => {
      if (!slot.isFullDay) return false;
      const [year, month, day] = slot.date.split('-').map(Number);
      const slotDate = new Date(year, month - 1, day);
      return date.toDateString() === slotDate.toDateString();
    });
  };

  // Fetch courses and generate colors
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesData = await getAllCourses();
        setCourses(coursesData);
        setCourseColors(generateCourseColors(coursesData));
        setIsDataReady(true);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setIsDataReady(true); // Set to true even on error to prevent infinite loading
      }
    };

    fetchCourses();
  }, []);

  // Define fetchUnavailableSlots using useCallback
  const fetchUnavailableSlots = useCallback(async () => {
    if (!currentUser?.email) {
      console.log('No current user email found');
      return;
    }

    console.log('=== FETCHING UNAVAILABLE SLOTS ===');
    console.log('Fetching for user:', currentUser.email);

    try {
      const availabilityDoc = await getDoc(doc(getFirestore(), 'instructor_availability', currentUser.email));
      if (availabilityDoc.exists()) {
        const data = availabilityDoc.data();
        if (data.unavailableSlots) {
          console.log('Fetched unavailable slots from DB:', data.unavailableSlots);
          console.log('Number of slots fetched:', data.unavailableSlots.length);
          setUnavailableSlots(data.unavailableSlots);
        } else {
          console.log('No unavailable slots found in document');
          setUnavailableSlots([]);
        }
      } else {
        console.log('No availability document found for user:', currentUser.email);
        setUnavailableSlots([]);
      }
      console.log('=== FETCHING UNAVAILABLE SLOTS COMPLETED ===');
    } catch (error) {
      console.error('Error fetching unavailable slots:', error);
      toast({
        title: 'Error',
        description: 'Failed to load availability settings.',
        variant: 'destructive',
      });
    }
  }, [currentUser, getFirestore]);

  useEffect(() => {
    fetchUnavailableSlots();
  }, [fetchUnavailableSlots]);

  // Update the fetchSchedules function to generate unique event IDs
  const fetchSchedules = async () => {
    if (!currentUser?.email || !courses.length || !isDataReady) return;

    try {
      setLoading(true);
      const db = getFirestore();
      const instructor = await getUserByEmail(currentUser.email);
      
      if (!instructor) {
        throw new Error('Instructor not found');
      }

      // Get all enrollments for the instructor
      const enrollmentsRef = collection(db, "enrollments");
      const enrollmentsQuery = query(
        enrollmentsRef,
        where("instructorId", "==", instructor.id)
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EnrollmentData[];

      // Get all users (students) information
      const usersRef = collection(db, "users");
      const usersQuery = query(
        usersRef,
        where("role", "==", "student")
      );
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserData[];

      // Then fetch schedules
      const schedulesQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", instructor.id),
        where("status", "in", ["pending", "confirmed", "completed"])
      );

      const schedulesSnapshot = await getDocs(schedulesQuery);
      const schedules = await Promise.all(schedulesSnapshot.docs.map(async (doc) => {
        const data = doc.data();
        const enrollment = enrollments.find(e => e.id === data.enrollmentId);
        const student = users.find(u => u.id === data.studentId);
        const course = courses.find(c => c.id === enrollment?.courseId);

        // Check if this is a combined lesson
        const isCombinedLesson = enrollment?.courseType === "individual" || 
                               (enrollment?.courseName && 
                                (enrollment.courseName.toLowerCase().includes("blog") || 
                                 enrollment.courseName.toLowerCase().includes("combined")));

        // Get colors based on the course name
        const colors = isCombinedLesson ? 
          { backgroundColor: '#9C27B0', borderColor: '#7B1FA2' } : // Purple for combined lessons
          courseColors[course?.title || ''] || courseColors.default;

        return {
          id: doc.id,
          ...data,
          studentName: student?.firstName ? `${student.firstName} ${student.lastName}` : data.studentName,
          studentEmail: student?.email || data.studentEmail,
          courseName: isCombinedLesson ? "Combined" : (course?.title || "Unknown Course"),
          courseType: isCombinedLesson ? "Combined" : (course?.type || "Not specified"),
          courseSubtype: isCombinedLesson ? "Combined" : (course?.subtype || "Not specified"),
          totalHours: enrollment?.totalHours || course?.totalHours || 0,
          hoursTaken: enrollment?.hoursTaken || 0,
          hoursRemaining: enrollment?.hoursRemaining || (enrollment?.totalHours || 0) - (enrollment?.hoursTaken || 0),
          vehicleType: course?.vehicleType || "Not specified",
          licenseType: course?.licenseType || "Not specified",
          colors: colors
        };
      })) as Schedule[];

      // Group schedules by student and date
      const groupedSchedules = schedules.reduce((acc, schedule) => {
        const key = `${schedule.studentId}-${schedule.date}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(schedule);
        return acc;
      }, {} as Record<string, Schedule[]>);

      // Convert to calendar events with unique IDs
      const calendarEvents: CalendarEvent[] = Object.values(groupedSchedules).flatMap(studentSchedules => {
        return studentSchedules.map((schedule, index) => {
          try {
            if (!schedule.date || !schedule.startTime) {
              console.error('Invalid schedule data:', schedule);
              return null;
            }

            // Parse the date and time with validation
            const dateParts = schedule.date.split('-');
            if (dateParts.length !== 3) {
              console.error('Invalid date format:', schedule.date);
              return null;
            }

            const [yearStr, monthStr, dayStr] = dateParts;
            const year = parseInt(yearStr);
            const month = parseInt(monthStr);
            const day = parseInt(dayStr);

            // Validate date components
            if (isNaN(year) || isNaN(month) || isNaN(day) || 
                year < 2000 || year > 2100 || 
                month < 1 || month > 12 || 
                day < 1 || day > 31) {
              console.error('Invalid date components:', { year, month, day });
              return null;
            }

            const timeParts = schedule.startTime.split(':');
            if (timeParts.length !== 2) {
              console.error('Invalid time format:', schedule.startTime);
              return null;
            }

            const [hoursStr, minutesStr] = timeParts;
            const hours = parseInt(hoursStr);
            const minutes = parseInt(minutesStr);

            // Validate time components
            if (isNaN(hours) || isNaN(minutes) || 
                hours < 0 || hours > 23 || 
                minutes < 0 || minutes > 59) {
              console.error('Invalid time components:', { hours, minutes });
              return null;
            }

            // Create date with validated components
            const startDateTime = new Date(year, month - 1, day, hours, minutes);
            
            // Validate the date
            if (isNaN(startDateTime.getTime())) {
              console.error('Invalid date created:', startDateTime);
              return null;
            }

            const endDateTime = new Date(startDateTime);
            // Fix: Use minutes-based calculation for decimal durations (e.g., 1.5 hours)
            const durationInMinutes = (schedule.duration || 1) * 60;
            endDateTime.setMinutes(endDateTime.getMinutes() + durationInMinutes);
            
            // Debug logging for duration calculations
            console.log('📅 Calendar event duration calculation:', {
              scheduleId: schedule.id,
              studentName: schedule.studentName,
              startTime: schedule.startTime,
              duration: schedule.duration,
              durationInMinutes,
              startDateTime: startDateTime.toISOString(),
              endDateTime: endDateTime.toISOString(),
              calculatedEndTime: endDateTime.toTimeString().slice(0, 5)
            });
            
            // Generate a unique ID for each event
            const uniqueId = `${schedule.id}-${schedule.date}-${schedule.startTime}-${Date.now()}-${index}`;
            
            return {
              id: uniqueId,
              title: index === 0 ? schedule.studentName : schedule.courseName,
              start: startDateTime.toISOString(),
              end: endDateTime.toISOString(),
              backgroundColor: schedule.colors?.backgroundColor || '#3788d8',
              borderColor: schedule.colors?.borderColor || '#2c6cb0',
              textColor: '#ffffff',
              display: 'block' as const,
              extendedProps: {
                studentName: schedule.studentName || 'Student',
                studentEmail: schedule.studentEmail || '',
                courseName: schedule.courseName || '',
                courseType: schedule.courseType || '',
                courseSubtype: schedule.courseSubtype || '',
                totalHours: schedule.totalHours || 0,
                hoursTaken: schedule.hoursTaken || 0,
                hoursRemaining: schedule.hoursRemaining || 0,
                vehicleType: schedule.vehicleType || 'Not specified',
                licenseType: schedule.licenseType || 'Not specified',
                scheduleId: schedule.id,
                status: schedule.status || 'pending',
                venue: schedule.venue || 'Not specified'
              }
            };
          } catch (error) {
            console.error('Error processing schedule:', error);
            console.error('Schedule data:', schedule);
            return null;
          }
        }).filter((event): event is NonNullable<typeof event> => event !== null);
      });

      // Add unavailable slots as events with unique IDs
      const unavailableEvents: CalendarEvent[] = unavailableSlots.map(slot => {
        try {
          console.log('Processing unavailable slot:', slot);
          const [year, month, day] = slot.date.split('-').map(Number);
          
          // Handle full-day slots differently
          if (slot.isFullDay) {
            const startDateTime = new Date(year, month - 1, day, 0, 0);
            const endDateTime = new Date(year, month - 1, day, 23, 59);
            const uniqueId = `unavailable-full-${slot.date}-${Date.now()}-${Math.random()}`;

            console.log('Created full-day unavailable event for date:', slot.date);
            return {
              id: uniqueId,
              title: 'Unavailable (Full Day)',
              start: startDateTime.toISOString(),
              end: endDateTime.toISOString(),
              backgroundColor: '#4B5563',
              borderColor: '#374151',
              textColor: '#ffffff',
              display: 'block' as const,
              allDay: true, // Set to true for full-day events
              classNames: ['unavailable-full-day'],
              extendedProps: {
                studentName: 'Unavailable',
                studentEmail: '',
                courseName: 'Unavailable Time',
                courseType: 'unavailable',
                courseSubtype: 'unavailable',
                totalHours: 0,
                hoursTaken: 0,
                hoursRemaining: 0,
                vehicleType: 'N/A',
                licenseType: 'N/A',
                scheduleId: uniqueId,
                status: 'unavailable',
                venue: 'N/A',
                isUnavailable: true,
                isFullDay: true
              }
            };
          }

          // Handle regular time slots
          const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
          const [endHours, endMinutes] = slot.endTime.split(':').map(Number);
          
          // Create dates in local time to avoid timezone conversion issues
          const startDateTime = new Date(year, month - 1, day, startHours, startMinutes);
          const endDateTime = new Date(year, month - 1, day, endHours, endMinutes);
          const uniqueId = `unavailable-${slot.date}-${slot.startTime}-${slot.endTime}-${Date.now()}-${Math.random()}`;

          console.log('Created time-slot unavailable event for date:', slot.date, 'time:', slot.startTime, '-', slot.endTime);
          return {
            id: uniqueId,
            title: 'Unavailable',
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString(),
            backgroundColor: '#F5F5F5',
            borderColor: '#E0E0E0',
            textColor: '#757575',
            display: 'block' as const,
            classNames: ['unavailable-time-slot'],
            extendedProps: {
              studentName: 'Unavailable',
              studentEmail: '',
              courseName: 'Unavailable Time',
              courseType: 'unavailable',
              courseSubtype: 'unavailable',
              totalHours: 0,
              hoursTaken: 0,
              hoursRemaining: 0,
              vehicleType: 'N/A',
              licenseType: 'N/A',
              scheduleId: uniqueId,
              status: 'unavailable',
              venue: 'N/A',
              isUnavailable: true,
              isFullDay: false
            }
          };
        } catch (error) {
          console.error('Error processing unavailable slot:', error);
          console.error('Slot data:', slot);
          return null;
        }
      }).filter((event): event is NonNullable<typeof event> => event !== null);

      console.log('Created unavailable events:', unavailableEvents.length);
      console.log('Total calendar events (schedules + unavailable):', calendarEvents.length + unavailableEvents.length);

      // Add the events to the calendar
      setEvents([...calendarEvents, ...unavailableEvents]);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast({
        title: 'Error',
        description: 'Failed to load schedules.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Update useEffect to use the moved fetchSchedules function
  useEffect(() => {
    if (isInitialLoad && isDataReady) {
      fetchSchedules();
    }
  }, [currentUser, getUserByEmail, courses, courseColors, isInitialLoad, isDataReady]);

  // Add a separate useEffect to refetch schedules when unavailableSlots change
  // REMOVED: This was causing removed events to come back
  // useEffect(() => {
  //   if (isDataReady && !isInitialLoad && !isManuallyRemovingEvent) {
  //     fetchSchedules();
  //   }
  // }, [unavailableSlots, isDataReady, isInitialLoad, isManuallyRemovingEvent]);

  // Add handleDeleteUnavailability function
  const handleDeleteUnavailability = async (event: CalendarEvent) => {
    if (!currentUser?.email) return;

    try {
      console.log('=== DELETING UNAVAILABILITY ===');
      console.log('Event to delete:', event);
      
      const db = getFirestore();
      const availabilityRef = doc(db, 'instructor_availability', currentUser.email);
      const availabilityDoc = await getDoc(availabilityRef);
      
      if (!availabilityDoc.exists()) {
        console.log('No availability document found');
        return;
      }

      const currentSlots = availabilityDoc.data().unavailableSlots || [];
      console.log('Current slots in DB:', currentSlots);
      
      // Parse event date and time properly
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Debug the event parsing
      console.log('Raw event start:', event.start);
      console.log('Raw event end:', event.end);
      console.log('Parsed eventStart:', eventStart);
      console.log('Parsed eventEnd:', eventEnd);
      
      // Get the date in YYYY-MM-DD format
      const eventDate = format(eventStart, 'yyyy-MM-dd');
      
      // Convert to local time strings (HH:mm format)
      const eventStartTime = format(eventStart, 'HH:mm');
      const eventEndTime = format(eventEnd, 'HH:mm');
      
      console.log('Event date:', eventDate);
      console.log('Event start time (local):', eventStartTime);
      console.log('Event end time (local):', eventEndTime);

      // Remove the matching slot from the array
      const updatedSlots = currentSlots.filter((slot: any) => {
        console.log('Checking slot:', slot);
        
        // Check if this slot matches the event we want to delete
        if (slot.date !== eventDate) {
          console.log('Date mismatch, keeping slot');
          return true;
        }
        
        // For full-day slots
        if (slot.isFullDay) {
          // Check if the event is also full-day (spans 00:00 to 23:59)
          const isEventFullDay = (eventStartTime === '00:00' && eventEndTime === '23:59') || 
                                event.extendedProps.isFullDay;
          if (isEventFullDay) {
            console.log('Full-day slot match, removing');
            return false; // Remove this slot
          } else {
            console.log('Full-day slot but event is not full-day, keeping');
            return true; // Keep this slot
          }
        }
        
        // For time slots - check exact time match
        const slotStartTime = slot.startTime;
        const slotEndTime = slot.endTime;
        
        console.log('Comparing times:');
        console.log('  Slot:', slotStartTime, '-', slotEndTime);
        console.log('  Event:', eventStartTime, '-', eventEndTime);
        
        const timeMatch = slotStartTime === eventStartTime && slotEndTime === eventEndTime;
        
        if (timeMatch) {
          console.log('Time slot match, removing');
          return false; // Remove this slot
        } else {
          console.log('Time mismatch, keeping slot');
          return true; // Keep this slot
        }
      });

      console.log('Updated slots:', updatedSlots);
      console.log('Slots removed:', currentSlots.length - updatedSlots.length);

      // Update Firestore
      await setDoc(availabilityRef, {
        instructorEmail: currentUser.email,
        unavailableSlots: updatedSlots,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('Successfully updated Firestore');

      // Update local state immediately
      setUnavailableSlots(updatedSlots);
      
      // Remove the event from the calendar immediately
      setEvents(prevEvents => prevEvents.filter(e => e.id !== event.id));

      console.log('=== DELETING UNAVAILABILITY COMPLETED ===');

      toast({
        title: "Success",
        description: "Unavailability removed successfully.",
      });
    } catch (error) {
      console.error("Error deleting unavailability:", error);
      toast({
        title: "Error",
        description: "Failed to remove unavailability. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Update handleEventClick to handle unavailability deletion
  const handleEventClick = async (info: EventClickArg) => {
    const event = info.event;
    
    // If it's an unavailability event, show override dialog
    if (event.extendedProps.isUnavailable) {
      setSelectedUnavailableEvent({
        id: event.id,
        title: event.title,
        start: event.start!.toISOString(),
        end: event.end!.toISOString(),
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        textColor: event.textColor,
        display: 'block',
        extendedProps: event.extendedProps
      });
      setIsOverrideDialogOpen(true);
      return;
    }

    // Rest of the existing handleEventClick code for regular events
    setIsLoadingStudentDetails(true);
    setSelectedEvent(null);
    
    try {
      // Get the schedule document to get student details
      const db = getFirestore();
      const scheduleDoc = await getDoc(doc(db, "schedules", event.extendedProps.scheduleId));
      
      if (!scheduleDoc.exists()) {
        throw new Error("Schedule not found");
      }
      
      const scheduleData = scheduleDoc.data();
      
      if (!scheduleData.studentId) {
        throw new Error("Student ID not found in schedule");
      }

      // Get student details
      const studentDoc = await getDoc(doc(db, "users", scheduleData.studentId));
      const studentData = studentDoc.exists() ? studentDoc.data() : null;
      
      console.log('🔍 Student data from users collection:', studentData);
      console.log('🔍 Student data from schedule:', {
        studentPhone: scheduleData.studentPhone,
        studentAddress: scheduleData.studentAddress,
        studentGearbox: scheduleData.studentGearbox,
        studentPostcode: scheduleData.studentPostcode
      });

      // Get enrollment data using enrollmentId from schedule
      let enrollmentData = null;
      if (scheduleData.enrollmentId) {
        const enrollmentDoc = await getDoc(doc(db, "enrollments", scheduleData.enrollmentId));
        if (enrollmentDoc.exists()) {
          enrollmentData = enrollmentDoc.data();
        }
      }
      
      // If we don't have enrollment data from enrollmentId, try to find it using studentId
      if (!enrollmentData && scheduleData.studentId) {
        const enrollmentsQuery = query(
          collection(db, "enrollments"),
          where("studentId", "==", scheduleData.studentId)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        enrollmentData = enrollmentsSnapshot.docs[0]?.data();
      }

      // Get course details if we have a courseId
      let courseData = null;
      if (enrollmentData?.courseId) {
        const courseDoc = await getDoc(doc(db, "courses", enrollmentData.courseId));
        if (courseDoc.exists()) {
          courseData = courseDoc.data();
        }
      }
      
      // Helper function to check if it's a combined lesson
      const isCombinedLesson = (courseType: string | undefined, courseName: string | undefined) => {
        return courseType === "individual" || 
               (courseName && 
                (courseName.toLowerCase().includes("blog") || 
                 courseName.toLowerCase().includes("combined")));
      };

      const isCombined = isCombinedLesson(courseData?.type, enrollmentData?.courseName);
      
      // Log which data source is being used for each field
      console.log('📊 Data source mapping:');
      console.log('  Phone:', studentData?.phone ? 'users collection' : 'schedule document');
      console.log('  Address:', studentData?.address ? 'users collection' : 'schedule document');
      console.log('  Gearbox:', studentData?.gearbox ? 'users collection' : 'schedule document');
      console.log('  Postcode:', studentData?.postcode ? 'users collection' : 'schedule document');
      
    const calendarEvent: CalendarEvent = {
      id: event.id,
      title: event.title,
      start: new Date(event.start!).toISOString(),
      end: new Date(event.end!).toISOString(),
      backgroundColor: event.backgroundColor,
      borderColor: event.borderColor,
      textColor: event.textColor,
      display: 'block',
      extendedProps: {
          studentName: scheduleData.studentName || '',
          studentEmail: scheduleData.studentEmail || '',
          studentPhone: studentData?.phone || scheduleData.studentPhone || '',
          studentAddress: studentData?.address || scheduleData.studentAddress || '',
          gearbox: studentData?.gearbox || scheduleData.studentGearbox || '',
          licenseNumber: studentData?.licenseNumber || scheduleData.licenseNumber || '',
          courseName: isCombined ? "Combined" : (courseData?.title || enrollmentData?.courseName || scheduleData.courseName || ''),
          courseType: isCombined ? "Combined" : (courseData?.type || enrollmentData?.courseType || scheduleData.courseType || ''),
          courseSubtype: isCombined ? "Combined" : (courseData?.subtype || enrollmentData?.courseSubtype || scheduleData.courseSubtype || ''),
          totalHours: courseData?.totalHours || enrollmentData?.totalHours || scheduleData.totalHours || 0,
          hoursTaken: enrollmentData?.hoursTaken || scheduleData.hoursTaken || 0,
          hoursRemaining: enrollmentData?.hoursRemaining || scheduleData.hoursRemaining || 0,
          vehicleType: courseData?.vehicleType || enrollmentData?.vehicleType || scheduleData.vehicleType || '',
          licenseType: courseData?.licenseType || enrollmentData?.licenseType || scheduleData.licenseType || '',
        scheduleId: event.extendedProps.scheduleId || '',
          status: scheduleData.status || '',
          venue: scheduleData.venue || '',
          duration: scheduleData.duration || 1,
          notes: scheduleData.notes || '',
          completedLessons: enrollmentData?.completedLessons || 0,
          totalLessons: enrollmentData?.totalLessons || courseData?.totalLessons || 0,
          lastLessonDate: enrollmentData?.lastLessonDate || '',
          studentPostcode: studentData?.postcode || scheduleData.studentPostcode || '',
        }
          };
      
    setSelectedEvent(calendarEvent);
      } catch (error) {
      console.error("Error loading student details:", error);
      toast({
        title: "Error",
        description: "Failed to load student details. Please try again.",
        variant: "destructive",
      });
      } finally {
        setIsLoadingStudentDetails(false);
    setIsDialogOpen(true);
    }
  };

  const handleDateSelect = async (selectInfo: DateSelectArg) => {
    const clickedDate = selectInfo.start;
    const clickedStartTime = format(selectInfo.start, 'HH:mm');
    const clickedEndTime = format(selectInfo.end, 'HH:mm');
    
    setSelectedDate(clickedDate);
    setSelectedDateForBooking(clickedDate);
    setStartTime(clickedStartTime);
    setEndTime(clickedEndTime);
    setIsFullDay(false);
    
    // Also set the bookingData with the selected time
    setBookingData({
      date: format(clickedDate, 'yyyy-MM-dd'),
      startTime: clickedStartTime,
      duration: "1",
      venue: "",
      notes: "",
    });
    
    try {
      const db = getFirestore();
      const dateStr = format(clickedDate, 'yyyy-MM-dd');
      
      // Check instructor's unavailability first
      const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      
      if (availabilityDoc.exists()) {
        const unavailableSlots = availabilityDoc.data().unavailableSlots || [];
        const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === dateStr);
        
        // If there's a full day unavailability, don't show the dialog
        if (dateUnavailableSlots.some((slot: any) => slot.isFullDay)) {
          toast({
            title: "Unavailable",
            description: "You are unavailable for the entire day.",
            variant: "destructive",
          });
          return;
        }
      }

      // Show a dialog to choose between booking and unavailability
      const dialog = document.createElement('div');
      dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      dialog.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
          <h3 class="text-lg font-semibold mb-4">What would you like to do?</h3>
          <div class="space-y-3">
            <button id="bookLesson" class="w-full bg-drivewise-600 text-white py-2 px-4 rounded hover:bg-drivewise-700 flex items-center justify-center">
              <span>Book a Lesson</span>
            </button>
            <button id="setUnavailability" class="w-full bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700">
              Set Unavailability
            </button>
            <button id="cancel" class="w-full border border-gray-300 py-2 px-4 rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      `;

      document.body.appendChild(dialog);

      dialog.querySelector('#bookLesson')?.addEventListener('click', () => {
        document.body.removeChild(dialog);
        setIsLoadingBooking(true);
        setIsBookingDialogOpen(true);
        fetchStudents().finally(() => {
          setIsLoadingBooking(false);
        });
      });

      dialog.querySelector('#setUnavailability')?.addEventListener('click', () => {
        document.body.removeChild(dialog);
        setIsUnavailabilityDialogOpen(true);
      });

      dialog.querySelector('#cancel')?.addEventListener('click', () => {
        document.body.removeChild(dialog);
      });
    } catch (error) {
      console.error('Error checking availability:', error);
      toast({
        title: "Error",
        description: "Failed to check availability. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSetUnavailability = async () => {
    if (!selectedDate || !startTime || !endTime || !currentUser?.email) return;

    try {
      setIsSubmitting(true);
      const db = getFirestore();
      const availabilityRef = doc(db, 'instructor_availability', currentUser.email);

      // Get current slots
      const availabilityDoc = await getDoc(availabilityRef);
      const currentSlots = availabilityDoc.exists() ? availabilityDoc.data()?.unavailableSlots || [] : [];

      // Log repeatSettings for debugging
      console.log('=== REPEAT UNAVAILABILITY DEBUG ===');
      console.log('Repeat settings:', repeatSettings);
      console.log('Selected date:', selectedDate);
      console.log('Start time:', startTime);
      console.log('End time:', endTime);
      console.log('Is full day:', isFullDay);
      console.log('Current slots in DB:', currentSlots);

      // Create new slots based on repeat settings
      const newSlots = [];
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const endDate = repeatSettings.type === 'none' ? new Date(startDate) : new Date(repeatSettings.endDate || startDate);
      endDate.setHours(0, 0, 0, 0); // Normalize to midnight
      let currentDate = new Date(startDate);

      console.log('Start date for repeat:', startDate);
      console.log('End date for repeat:', endDate);
      console.log('Repeat type:', repeatSettings.type);

      if (repeatSettings.type === 'none') {
        // Only one slot
        const slot = {
          date: format(currentDate, 'yyyy-MM-dd'),
          startTime: isFullDay ? '00:00' : startTime,
          endTime: isFullDay ? '23:59' : endTime,
          isFullDay: isFullDay,
          reason: unavailabilityReason || 'Unavailable'
        };
        newSlots.push(slot);
        console.log('Single slot created:', slot);
      } else {
        // Repeat until endDate
        let slotCount = 0;
        while (currentDate <= endDate) {
          const slot = {
            date: format(currentDate, 'yyyy-MM-dd'),
            startTime: isFullDay ? '00:00' : startTime,
            endTime: isFullDay ? '23:59' : endTime,
            isFullDay: isFullDay,
            reason: unavailabilityReason || 'Unavailable'
          };
          newSlots.push(slot);
          slotCount++;
          console.log(`Slot ${slotCount} created for date ${format(currentDate, 'yyyy-MM-dd')}:`, slot);
          
          switch (repeatSettings.type) {
            case 'daily':
              currentDate.setDate(currentDate.getDate() + 1);
              break;
            case 'weekly':
              currentDate.setDate(currentDate.getDate() + 7);
              break;
            case 'biweekly':
              currentDate.setDate(currentDate.getDate() + 14);
              break;
            case 'monthly':
              currentDate.setMonth(currentDate.getMonth() + 1);
              break;
            default:
              break;
          }
        }
        console.log(`Total ${slotCount} slots created for repeat pattern`);
      }

      // Log the generated slots for debugging
      console.log('Generated unavailability slots:', newSlots);

      // Update the document with new slots in Firestore
      const updatedSlots = [...currentSlots, ...newSlots];
      console.log('Total slots to save to DB:', updatedSlots.length);
      console.log('All slots being saved:', updatedSlots);
      
      await setDoc(availabilityRef, {
        instructorEmail: currentUser.email,
        unavailableSlots: updatedSlots,
        updatedAt: serverTimestamp()
      }, { merge: true });

      console.log('Successfully saved to Firestore');

      // Update local state and refresh calendar instantly
      setUnavailableSlots(updatedSlots);
      console.log('Updated local unavailableSlots state');

      // Add new unavailability slots to events state for instant UI update
      const newUnavailableEvents = newSlots.map(slot => {
        const [year, month, day] = slot.date.split('-').map(Number);
        const startDateTime = new Date(year, month - 1, day, ...slot.startTime.split(':').map(Number));
        const endDateTime = new Date(year, month - 1, day, ...slot.endTime.split(':').map(Number));
        return {
          id: `unavailable-${slot.date}-${slot.startTime}-${slot.endTime}-${Math.random()}`,
          title: slot.isFullDay ? 'Unavailable (Full Day)' : 'Unavailable',
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
          backgroundColor: slot.isFullDay ? '#4B5563' : '#F5F5F5',
          borderColor: slot.isFullDay ? '#374151' : '#E0E0E0',
          textColor: slot.isFullDay ? '#ffffff' : '#757575',
          display: 'block' as const,
          allDay: false, // Ensure full-day events show in time grid
          classNames: [slot.isFullDay ? 'unavailable-full-day' : 'unavailable-time-slot'],
          extendedProps: {
            studentName: 'Unavailable',
            courseName: 'Unavailable Time',
            courseType: 'unavailable',
            isUnavailable: true,
            isFullDay: slot.isFullDay,
            reason: slot.reason,
          }
        };
      });

      console.log('Created calendar events for new slots:', newUnavailableEvents);

      setEvents(prevEvents => {
        const updatedEvents = [...prevEvents, ...newUnavailableEvents];
        console.log('Updated events state, total events:', updatedEvents.length);
        return updatedEvents;
      });

      // No fetchSchedules here for instant feedback

      // Close dialog and reset form
      setIsUnavailabilityDialogOpen(false);
      setSelectedDate(null);
      setStartTime('09:00');
      setEndTime('17:00');
      setIsFullDay(false);
      setUnavailabilityReason('');
      setRepeatSettings({ type: 'none' });

      console.log('=== REPEAT UNAVAILABILITY COMPLETED ===');

      toast({
        title: "Success",
        description: "Unavailability set successfully.",
      });
    } catch (error) {
      console.error("Error setting unavailability:", error);
      toast({
        title: "Error",
        description: "Failed to set unavailability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const db = getFirestore();
      // Use instructorEmail for enrollments
      const instructorIdentifier = currentUser?.email;
      const enrollmentsQuery = query(
        collection(db, "enrollments"),
        where("instructorEmail", "==", instructorIdentifier)
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Enrollment[];
      const studentMap = new Map();
      for (const enrollment of enrollments) {
        // Use userId for users (not studentId)
        const studentQuery = query(
          collection(db, "users"),
          where("id", "==", enrollment.userId)
        );
        const studentSnapshot = await getDocs(studentQuery);
        const studentData = studentSnapshot.docs[0]?.data();
        if (!studentData) {
          console.error(`Student data not found for user ID: ${enrollment.userId}`);
          continue;
        }
        if (studentMap.has(studentData.id)) {
          const existingStudent = studentMap.get(studentData.id);
          existingStudent.enrollments.push(enrollment);
        } else {
          const uniqueId = `${studentData.id}-${Date.now()}-${Math.random()}`;
          studentMap.set(studentData.id, {
            id: uniqueId,
            originalId: studentData.id,
            firstName: studentData.firstName || "",
            lastName: studentData.lastName || "",
            email: studentData.email || "",
            phone: studentData.phone || "",
            address: studentData.address || "",
            postcode: studentData.postcode || "",
            gearbox: studentData.gearbox || "",
            licenseNumber: studentData.licenseNumber || "",
            enrollments: [enrollment]
          });
        }
      }
      setStudents(Array.from(studentMap.values()));
    } catch (error) {
      console.error("Error fetching students:", error);
      toast({
        title: "Error",
        description: "Failed to load students. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBooking = async () => {
    if (!selectedStudent || !selectedEnrollment) return;

    console.log('=== STARTING BOOKING PROCESS ===');
    console.log('Booking data:', bookingData);
    console.log('Selected student:', selectedStudent);
    console.log('Selected enrollment:', selectedEnrollment);
    console.log('Is booking from override:', isBookingFromOverride);

    // If booking from override, check that the booking fits within the unavailable slot
    if (isBookingFromOverride && selectedUnavailableEvent) {
      const slotStart = format(new Date(selectedUnavailableEvent.start), 'HH:mm');
      const slotEnd = format(new Date(selectedUnavailableEvent.end), 'HH:mm');
      const [slotStartHour, slotStartMinute] = slotStart.split(':').map(Number);
      const [slotEndHour, slotEndMinute] = slotEnd.split(':').map(Number);
      const slotStartMinutes = slotStartHour * 60 + slotStartMinute;
      const slotEndMinutes = slotEndHour * 60 + slotEndMinute;
      const [bookingStartHour, bookingStartMinute] = (bookingData.startTime || slotStart).split(':').map(Number);
      const bookingStartMinutes = bookingStartHour * 60 + bookingStartMinute;
      const bookingDuration = parseFloat(bookingData.duration);
      const bookingEndMinutes = bookingStartMinutes + bookingDuration * 60;
      
      console.log('Override slot validation:', {
        slotStart,
        slotEnd,
        bookingStart: bookingData.startTime,
        bookingDuration,
        bookingEndMinutes,
        slotStartMinutes,
        slotEndMinutes
      });
      
      if (
        bookingStartMinutes < slotStartMinutes ||
        bookingEndMinutes > slotEndMinutes
      ) {
        console.log('Booking rejected: outside override slot');
        toast({
          title: "Error",
          description: "Booking must be within the unavailable slot.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsLoadingBooking(true);
    try {
      // Check if booking is for a past date
      const bookingDate = new Date(bookingData.date);
      const bookingDateTime = new Date(`${bookingData.date}T${bookingData.startTime}`);
      const now = new Date();
      
      if (bookingDateTime < now) {
        if (!allowPastDateBooking) {
          toast({
            title: "Past Date Not Allowed",
            description: "Cannot book lessons for past dates. Enable 'Allow Past Date Booking' to record past lessons.",
            variant: "destructive",
          });
          setIsLoadingBooking(false);
          return;
        } else {
          // Show warning for past date booking
          const shouldProceed = window.confirm(
            `You are booking a lesson for a past date (${format(bookingDateTime, 'EEEE, MMMM d, yyyy')} at ${bookingData.startTime}). This is typically used to record lessons that were already conducted. Do you want to proceed?`
          );
          
          if (!shouldProceed) {
            setIsLoadingBooking(false);
            return;
          }
        }
      }

      // Only check general availability if NOT booking from override
      if (!isBookingFromOverride) {
        console.log('Checking general availability...');
        const isAvailable = await checkAvailability(
          bookingData.date,
          bookingData.startTime,
          parseInt(bookingData.duration)
        );
        console.log('Availability check result:', isAvailable);
        if (!isAvailable) {
          console.log('Booking rejected: not available');
          setIsLoadingBooking(false);
          return;
        }
      } else {
        console.log('Skipping availability check for override booking');
      }
      
      console.log('Selected Student:', selectedStudent); // Debug log
      console.log('Selected Enrollment:', selectedEnrollment); // Debug log

      const db = getFirestore();
      
      // Get student details using originalId
      const studentQuery = query(
        collection(db, "users"),
        where("id", "==", selectedStudent.originalId)
      );
      const studentSnapshot = await getDocs(studentQuery);
      console.log('Student Query Result:', studentSnapshot.docs.map(doc => doc.data())); // Debug log

      if (studentSnapshot.empty) {
        // Try alternative query if the first one fails
        const altStudentQuery = query(
          collection(db, "users"),
          where("email", "==", selectedStudent.email)
        );
        const altStudentSnapshot = await getDocs(altStudentQuery);
        console.log('Alternative Student Query Result:', altStudentSnapshot.docs.map(doc => doc.data())); // Debug log
        
        if (altStudentSnapshot.empty) {
          throw new Error("Student data not found");
        }
        const studentData = altStudentSnapshot.docs[0].data();
        console.log('Found student data:', studentData); // Debug log

        const studentName = `${studentData.firstName} ${studentData.lastName}`;
        const duration = parseInt(bookingData.duration);
        const startTime = bookingData.startTime;
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const startMinutesTotal = startHours * 60 + startMinutes;
        const endMinutesTotal = startMinutesTotal + (duration * 60);
        const endHours = Math.floor(endMinutesTotal / 60);
        const endMinutes = endMinutesTotal % 60;
        const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        // Update enrollment hours
        const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
        const enrollmentDoc = await getDoc(enrollmentRef);
        const enrollmentData = enrollmentDoc.exists() ? enrollmentDoc.data() : null;

        if (enrollmentData) {
          const newHoursTaken = (enrollmentData.hoursTaken || 0) + duration;
          const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

          await updateDoc(enrollmentRef, {
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining,
            lastLessonDate: bookingData.date
          });
        }

        const newBooking = {
          instructorId: currentUser?.uid,
          instructorEmail: currentUser?.email,
          instructorName: currentUser?.displayName || '',
          instructorNotified: true,
          enrollmentId: selectedEnrollment.id,
          studentId: studentData.id, // Use the ID from the found student data
          studentName: studentName,
          studentEmail: studentData.email,
          studentPhone: studentData.phone || '',
          studentAddress: studentData.address || '',
          studentPostcode: studentData.postcode || '',
          studentGearbox: studentData.gearbox || '',
          courseName: selectedEnrollment.courseName,
          courseType: selectedEnrollment.courseType,
          courseSubtype: selectedEnrollment.courseSubtype || '',
          date: bookingData.date,
          startTime: startTime,
          endTime: endTime,
          startMinutes: startMinutesTotal,
          endMinutes: endMinutesTotal,
          duration: duration,
          venue: bookingData.venue,
          notes: bookingData.notes,
          status: "confirmed",
          createdAt: new Date().toISOString(),
          totalHours: selectedEnrollment.totalHours || 0,
          hoursTaken: selectedEnrollment.hoursTaken || 0,
          hoursRemaining: selectedEnrollment.hoursRemaining || 0,
          vehicleType: selectedEnrollment.vehicleType || 'Not specified',
          licenseType: selectedEnrollment.licenseType || 'Not specified'
        };

        console.log('Creating booking with data:', newBooking);
        await addDoc(collection(db, "schedules"), newBooking);
        console.log('Booking created successfully');

        toast({
          title: "Success",
          description: "Lesson booked successfully!",
        });

        setIsBookingDialogOpen(false);
        setSelectedStudent(null);
        setSelectedEnrollment(null);
        setBookingData({
          date: "",
          startTime: "",
          duration: "1",
          venue: "",
          notes: "",
        });
        setIsBookingFromOverride(false);

        // Refresh the calendar
        await fetchSchedules();
      } else {
        const studentData = studentSnapshot.docs[0].data();
        console.log('Found student data:', studentData); // Debug log

        const studentName = `${studentData.firstName} ${studentData.lastName}`;
        const duration = parseInt(bookingData.duration);
        const startTime = bookingData.startTime;
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const startMinutesTotal = startHours * 60 + startMinutes;
        const endMinutesTotal = startMinutesTotal + (duration * 60);
        const endHours = Math.floor(endMinutesTotal / 60);
        const endMinutes = endMinutesTotal % 60;
        const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        // Update enrollment hours
        const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
        const enrollmentDoc = await getDoc(enrollmentRef);
        const enrollmentData = enrollmentDoc.exists() ? enrollmentDoc.data() : null;

        if (enrollmentData) {
          const newHoursTaken = (enrollmentData.hoursTaken || 0) + duration;
          const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

          await updateDoc(enrollmentRef, {
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining,
            lastLessonDate: bookingData.date
          });
        }

        const newBooking = {
          instructorId: currentUser?.uid,
          instructorEmail: currentUser?.email,
          instructorName: currentUser?.displayName || '',
          instructorNotified: true,
          enrollmentId: selectedEnrollment.id,
          studentId: studentData.id,
          studentName: studentName,
          studentEmail: studentData.email,
          studentPhone: studentData.phone || '',
          studentAddress: studentData.address || '',
          studentPostcode: studentData.postcode || '',
          studentGearbox: studentData.gearbox || '',
          courseName: selectedEnrollment.courseName,
          courseType: selectedEnrollment.courseType,
          courseSubtype: selectedEnrollment.courseSubtype || '',
          date: bookingData.date,
          startTime: startTime,
          endTime: endTime,
          startMinutes: startMinutesTotal,
          endMinutes: endMinutesTotal,
          duration: duration,
          venue: bookingData.venue,
          notes: bookingData.notes,
          status: "confirmed",
          createdAt: new Date().toISOString(),
          totalHours: selectedEnrollment.totalHours || 0,
          hoursTaken: selectedEnrollment.hoursTaken || 0,
          hoursRemaining: selectedEnrollment.hoursRemaining || 0,
          vehicleType: selectedEnrollment.vehicleType || 'Not specified',
          licenseType: selectedEnrollment.licenseType || 'Not specified'
        };

        console.log('Creating booking with data:', newBooking);
        await addDoc(collection(db, "schedules"), newBooking);
        console.log('Booking created successfully');

        toast({
          title: "Success",
          description: "Lesson booked successfully!",
        });

        setIsBookingDialogOpen(false);
        setSelectedStudent(null);
        setSelectedEnrollment(null);
        setBookingData({
          date: "",
          startTime: "",
          duration: "1",
          venue: "",
          notes: "",
        });
        setIsBookingFromOverride(false);

        // Refresh the calendar
        await fetchSchedules();
      }
      
      console.log('=== BOOKING PROCESS COMPLETED ===');
    } catch (error) {
      console.error("Error booking lesson:", error);
      toast({
        title: "Error",
        description: "Failed to book lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBooking(false);
    }
  };

  // Update the generateTimeSlots function to exclude booked times
  const generateTimeSlots = async (date: string) => {
    try {
      if (!currentUser?.uid) {
        console.error('No current user found');
        return [];
      }

      const db = getFirestore();
      const slots = [];
      const bookedTimes = new Set();

      // Get instructor's bookings for the day
      const instructorBookingsQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", currentUser?.uid),
        where("date", "==", date),
        where("status", "in", ["pending", "confirmed", "completed"])
      );
      const instructorBookingsSnapshot = await getDocs(instructorBookingsQuery);
      const instructorBookings = instructorBookingsSnapshot.docs.map(doc => doc.data());

      // Get instructor's unavailability
      const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      const unavailableSlots = availabilityDoc.exists() ? availabilityDoc.data().unavailableSlots || [] : [];
      const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === date);

      // Add booked times to the set
      instructorBookings.forEach(booking => {
        // Skip cancelled lessons - they should not block new bookings
        if (booking.status === 'cancelled') {
          return;
        }
        
        const startTime = booking.startTime;
        const duration = booking.duration || 1;
        const [hours, minutes] = startTime.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + (duration * 60);

        for (let m = startMinutes; m < endMinutes; m += 15) {
          const h = Math.floor(m / 60);
          const min = m % 60;
          bookedTimes.add(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
        }
      });

      // Add unavailable times to the set
      dateUnavailableSlots.forEach((slot: any) => {
        if (!slot.isFullDay) {
          const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
          const [endHours, endMinutes] = slot.endTime.split(':').map(Number);
          const startTotalMinutes = startHours * 60 + startMinutes;
          const endTotalMinutes = endHours * 60 + endMinutes;

          for (let m = startTotalMinutes; m < endTotalMinutes; m += 15) {
            const h = Math.floor(m / 60);
            const min = m % 60;
            bookedTimes.add(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
          }
        }
      });

      // Generate available time slots
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMinute = minute.toString().padStart(2, '0');
          const timeSlot = `${formattedHour}:${formattedMinute}`;
          
          if (!bookedTimes.has(timeSlot)) {
            slots.push(timeSlot);
          }
        }
      }

      return slots;
    } catch (error) {
      console.error('Error generating time slots:', error);
      return [];
    }
  };

  // Update the time slot selection in the booking dialog
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (isBookingDialogOpen && bookingData.date) {
        const slots = await generateTimeSlots(bookingData.date);
        setAvailableTimeSlots(slots);
      }
    };
    
    fetchTimeSlots();
  }, [isBookingDialogOpen, bookingData.date]);

  // Update the checkAvailability function to properly check instructor's unavailability from the correct collection
  const checkAvailability = async (date: string, startTime: string, duration: number, excludeScheduleId?: string) => {
    setIsCheckingAvailability(true);
    setAvailabilityError("");
    
    try {
      const db = getFirestore();
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 60 * 1000);

      // Check instructor's unavailability from instructor_availability collection
      const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      
      if (availabilityDoc.exists()) {
        const unavailableSlots = availabilityDoc.data().unavailableSlots || [];
        
        // Check if the selected date has any unavailability
        const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === date);
        
        for (const slot of dateUnavailableSlots) {
          if (slot.isFullDay) {
            setAvailabilityError("Instructor is unavailable for the entire day");
            return false;
          }

          const slotStart = new Date(`${date}T${slot.startTime}`);
          const slotEnd = new Date(`${date}T${slot.endTime}`);
          
          if (
            (startDateTime >= slotStart && startDateTime < slotEnd) ||
            (endDateTime > slotStart && endDateTime <= slotEnd) ||
            (startDateTime <= slotStart && endDateTime >= slotEnd)
          ) {
            setAvailabilityError(`Instructor is unavailable from ${slot.startTime} to ${slot.endTime}`);
            return false;
          }
        }
      }

      // Check student's existing bookings across all instructors
      if (selectedStudent) {
        const studentBookingsQuery = query(
          collection(db, "schedules"),
          where("studentId", "==", selectedStudent.originalId),
          where("date", "==", date),
          where("status", "in", ["pending", "confirmed", "completed"])
        );
        
        const studentBookingsSnapshot = await getDocs(studentBookingsQuery);
        const studentBookings = studentBookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Array<{ id: string; status: string; startTime: string; endTime: string; duration: number }>;

        for (const booking of studentBookings) {
          // Skip the current lesson being moved
          if (excludeScheduleId && booking.id === excludeScheduleId) {
            continue;
          }
          
          // Skip cancelled lessons - they should not block new bookings
          if (booking.status === 'cancelled') {
            continue;
          }
          
                  const bookingStart = new Date(`${date}T${booking.startTime}`);
        const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
        // Add buffer time to student conflict check - students need 15-minute break between lessons
        const bookingEndWithBuffer = new Date(bookingEnd.getTime() + (LESSON_BUFFER_MINUTES * 60 * 1000));
        
        if (
          (startDateTime >= bookingStart && startDateTime < bookingEndWithBuffer) ||
          (endDateTime > bookingStart && endDateTime <= bookingEndWithBuffer) ||
          (startDateTime <= bookingStart && endDateTime >= bookingEndWithBuffer)
        ) {
          setAvailabilityError(`Student already has a booking from ${booking.startTime} to ${format(bookingEnd, 'HH:mm')} (with 15-minute buffer)`);
          return false;
        }
        }
      }

      // Check instructor's existing bookings
      const instructorBookingsQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", currentUser?.uid),
        where("date", "==", date),
        where("status", "in", ["pending", "confirmed", "completed"])
      );
      
      const instructorBookingsSnapshot = await getDocs(instructorBookingsQuery);
      const instructorBookings = instructorBookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Array<{ id: string; status: string; startTime: string; endTime: string; duration: number }>;

      for (const booking of instructorBookings) {
        // Skip the current lesson being moved
        if (excludeScheduleId && booking.id === excludeScheduleId) {
          continue;
        }
        
        // Skip cancelled lessons - they should not block new bookings
        if (booking.status === 'cancelled') {
          continue;
        }
        
        const bookingStart = new Date(`${date}T${booking.startTime}`);
        const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
        // Add buffer time to instructor conflict check - instructors need 15-minute break between lessons
        const bookingEndWithBuffer = new Date(bookingEnd.getTime() + (LESSON_BUFFER_MINUTES * 60 * 1000));
        
        if (
          (startDateTime >= bookingStart && startDateTime < bookingEndWithBuffer) ||
          (endDateTime > bookingStart && endDateTime <= bookingEndWithBuffer) ||
          (startDateTime <= bookingStart && endDateTime >= bookingEndWithBuffer)
        ) {
          setAvailabilityError(`You already have a booking from ${booking.startTime} to ${format(bookingEnd, 'HH:mm')} (with 15-minute buffer)`);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error checking availability:", error);
      setAvailabilityError("Error checking availability. Please try again.");
      return false;
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Initialize time slots
  useEffect(() => {
    const initializeTimeSlots = async () => {
      const today = new Date().toISOString().split('T')[0];
      const slots = await generateTimeSlots(today);
      setTimeSlots(slots);
    };
    
    initializeTimeSlots();
  }, []);

  // Add state for view mode
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');

  // Add function to group events by date
  const getEventsByDate = () => {
    const groupedEvents: { [key: string]: CalendarEvent[] } = {};
    
    events.forEach(event => {
      const date = event.start.split('T')[0];
      if (!groupedEvents[date]) {
        groupedEvents[date] = [];
      }
      groupedEvents[date].push(event);
    });

    return Object.entries(groupedEvents)
      .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime());
  };

  // Add this helper function near the top of the component
  const createEventClickArg = (event: CalendarEvent): EventClickArg => ({
    event: event as any,
    el: document.createElement('div'),
    jsEvent: new MouseEvent('click'),
    view: {
      type: 'timeGridWeek',
      calendar: null as any,
      title: '',
      activeStart: new Date(),
      activeEnd: new Date(),
      currentStart: new Date(),
      currentEnd: new Date(),
    } as any,
  });

  // Add this helper function near the top of the component
  const handleListEventClick = (event: CalendarEvent) => {
    // Create a minimal event click arg that satisfies the type requirements
    const eventClickArg: EventClickArg = {
      event: event as any,
      el: document.createElement('div'),
      jsEvent: new MouseEvent('click'),
      view: {
        type: 'timeGridWeek',
        calendar: null,
        title: '',
        activeStart: new Date(),
        activeEnd: new Date(),
        currentStart: new Date(),
        currentEnd: new Date(),
      } as any,
    };
    handleEventClick(eventClickArg);
  };

  // Add this interface near other interfaces
  interface UnavailabilityRepeat {
    type: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
    endDate?: Date;
  }

  // Update the state in the component
  const [unavailabilityRepeat, setUnavailabilityRepeat] = useState<UnavailabilityRepeat>({ type: 'none' });

  // Add handleCancelSchedule function
  const handleCancelSchedule = async () => {
    if (!selectedEvent?.extendedProps.scheduleId) {
      toast({
        title: "Error",
        description: "Schedule ID not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    const shouldCancel = window.confirm(
      `Are you sure you want to cancel the lesson for ${selectedEvent.extendedProps.studentName} on ${format(new Date(selectedEvent.start), "MMMM d, yyyy")} at ${format(new Date(selectedEvent.start), "HH:mm")}?`
    );

    if (!shouldCancel) return;

    setIsCancelling(true);
    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", selectedEvent.extendedProps.scheduleId);
      
      // Update the schedule status to cancelled
      await updateDoc(scheduleRef, {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: currentUser?.email || currentUser?.uid
      });

      // If this was a confirmed lesson, we need to refund the hours to the student's enrollment
      if (selectedEvent.extendedProps.status === "confirmed" && selectedEvent.extendedProps.duration) {
        // Get the schedule document to find the enrollment ID
        const scheduleDoc = await getDoc(scheduleRef);
        if (scheduleDoc.exists()) {
          const scheduleData = scheduleDoc.data();
          const enrollmentId = scheduleData.enrollmentId;
          
          if (enrollmentId) {
            const enrollmentRef = doc(db, "enrollments", enrollmentId);
            const enrollmentDoc = await getDoc(enrollmentRef);
            
            if (enrollmentDoc.exists()) {
              const enrollmentData = enrollmentDoc.data();
              const newHoursTaken = Math.max(0, (enrollmentData.hoursTaken || 0) - selectedEvent.extendedProps.duration);
              const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

              await updateDoc(enrollmentRef, {
                hoursTaken: newHoursTaken,
                hoursRemaining: newHoursRemaining
              });
            }
          }
        }
      }

      toast({
        title: "Success",
        description: "Lesson cancelled successfully.",
      });

      // Close the dialog and refresh the calendar
      setIsDialogOpen(false);
      setSelectedEvent(null);
      await fetchSchedules();

    } catch (error) {
      console.error("Error cancelling schedule:", error);
      toast({
        title: "Error",
        description: "Failed to cancel lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // Generate report function (supports daily, weekly, monthly)
  const generateReport = async (type: 'daily' | 'weekly' | 'monthly' = 'weekly') => {
    setIsGeneratingReport(true);
    try {
      const today = new Date();
      let startDate: Date;
      let endDate: Date;
      let title: string;

      switch (type) {
        case 'daily':
          startDate = new Date(today);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          title = `Daily Report - ${format(today, 'EEEE, MMMM d, yyyy')}`;
          break;
        case 'weekly':
          startDate = startOfWeek(today, { weekStartsOn: 1 }); // Monday
          endDate = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
          title = `Weekly Report - ${format(startDate, 'MMM d')} to ${format(endDate, 'MMM d, yyyy')}`;
          break;
        case 'monthly':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
          title = `Monthly Report - ${format(today, 'MMMM yyyy')}`;
          break;
        default:
          startDate = startOfWeek(today, { weekStartsOn: 1 });
          endDate = endOfWeek(today, { weekStartsOn: 1 });
          title = `Weekly Report - ${format(startDate, 'MMM d')} to ${format(endDate, 'MMM d, yyyy')}`;
      }

      // Filter events for the selected period
      const periodEvents = events.filter(event => {
        const eventDate = parseISO(event.start);
        return eventDate >= startDate && eventDate <= endDate && !event.extendedProps.isUnavailable;
      });

      // Generate daily breakdown
      const daysInPeriod = eachDayOfInterval({ start: startDate, end: endDate });
      const eventsByDay = daysInPeriod.map(day => {
        const dayEvents = periodEvents.filter(event => 
          format(parseISO(event.start), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
        );
        
        return {
          date: format(day, 'yyyy-MM-dd'),
          dayName: format(day, 'EEEE'),
          events: dayEvents,
          totalLessons: dayEvents.length,
          totalHours: dayEvents.reduce((total, event) => total + (event.extendedProps.duration || 1), 0),
          confirmedLessons: dayEvents.filter(event => event.extendedProps.status === 'confirmed').length,
          pendingLessons: dayEvents.filter(event => event.extendedProps.status === 'pending').length,
          completedLessons: dayEvents.filter(event => event.extendedProps.status === 'completed').length
        };
      });

      // Calculate period totals
      const periodTotals = {
        totalLessons: periodEvents.length,
        totalHours: periodEvents.reduce((total, event) => total + (event.extendedProps.duration || 1), 0),
        confirmedLessons: periodEvents.filter(event => event.extendedProps.status === 'confirmed').length,
        pendingLessons: periodEvents.filter(event => event.extendedProps.status === 'pending').length,
        completedLessons: periodEvents.filter(event => event.extendedProps.status === 'completed').length,
        uniqueStudents: new Set(periodEvents.map(event => event.extendedProps.studentName)).size
      };

      // Get student statistics
      const studentStats = periodEvents.reduce((acc, event) => {
        const studentName = event.extendedProps.studentName || 'Unknown';
        if (!acc[studentName]) {
          acc[studentName] = {
            name: studentName,
            lessons: 0,
            hours: 0,
            courses: new Set()
          };
        }
        acc[studentName].lessons++;
        acc[studentName].hours += event.extendedProps.duration || 1;
        acc[studentName].courses.add(event.extendedProps.courseName || 'Unknown');
        return acc;
      }, {} as Record<string, { name: string; lessons: number; hours: number; courses: Set<string> }>);

      // Convert courses Set to array
      Object.values(studentStats).forEach(student => {
        student.courses = Array.from(student.courses) as any;
      });

      const reportData = {
        type,
        title,
        startDate: format(startDate, 'MMMM d, yyyy'),
        endDate: format(endDate, 'MMMM d, yyyy'),
        generatedAt: format(new Date(), 'MMMM d, yyyy HH:mm'),
        instructorName: currentUser?.displayName || currentUser?.email || 'Unknown',
        dailyBreakdown: eventsByDay,
        periodTotals,
        studentStats: Object.values(studentStats),
        topStudents: Object.values(studentStats)
          .sort((a, b) => b.lessons - a.lessons)
          .slice(0, 5)
      };

      setWeeklyReportData(reportData);
      setIsWeeklyReportOpen(true);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Generate weekly report function (for backward compatibility)
  const generateWeeklyReport = async () => {
    await generateReport('weekly');
  };

  // Download PDF function
  const downloadPDF = async () => {
    if (!weeklyReportData) return;
    
    setIsDownloadingPDF(true);
    try {
      // Create a simple HTML content for the PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${weeklyReportData.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { margin-bottom: 30px; }
            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 20px; }
            .summary-card { border: 1px solid #ddd; padding: 15px; text-align: center; }
            .daily-breakdown { margin-bottom: 30px; }
            .day-section { margin-bottom: 20px; border: 1px solid #eee; padding: 15px; }
            .lesson-item { margin: 10px 0; padding: 10px; background: #f9f9f9; }
            .student-stats { margin-bottom: 30px; }
            .student-item { margin: 10px 0; padding: 10px; border: 1px solid #eee; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${weeklyReportData.title}</h1>
            <p>Generated on ${weeklyReportData.generatedAt}</p>
            <p>Instructor: ${weeklyReportData.instructorName}</p>
            <p>Period: ${weeklyReportData.startDate} - ${weeklyReportData.endDate}</p>
          </div>
          
          <div class="summary">
            <h2>Summary</h2>
            <div class="summary-grid">
              <div class="summary-card">
                <h3>Total Lessons</h3>
                <p>${weeklyReportData.periodTotals.totalLessons}</p>
              </div>
              <div class="summary-card">
                <h3>Total Hours</h3>
                <p>${weeklyReportData.periodTotals.totalHours}</p>
              </div>
              <div class="summary-card">
                <h3>Confirmed</h3>
                <p>${weeklyReportData.periodTotals.confirmedLessons}</p>
              </div>
              <div class="summary-card">
                <h3>Unique Students</h3>
                <p>${weeklyReportData.periodTotals.uniqueStudents}</p>
              </div>
            </div>
          </div>
          
          <div class="daily-breakdown">
            <h2>Daily Breakdown</h2>
            ${weeklyReportData.dailyBreakdown.map(day => `
              <div class="day-section">
                <h3>${day.dayName} - ${format(parseISO(day.date), 'MMMM d, yyyy')}</h3>
                <p>Lessons: ${day.totalLessons} | Hours: ${day.totalHours}</p>
                ${day.events.map(event => `
                  <div class="lesson-item">
                    <strong>${event.extendedProps.studentName}</strong> - ${event.extendedProps.courseName}<br>
                    Time: ${format(parseISO(event.start), 'HH:mm')} - ${format(parseISO(event.end), 'HH:mm')}<br>
                    Status: ${event.extendedProps.status} | Duration: ${event.extendedProps.duration || 1} hour(s)
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </div>
          
          <div class="student-stats">
            <h2>Student Statistics</h2>
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Lessons</th>
                  <th>Hours</th>
                  <th>Courses</th>
                </tr>
              </thead>
              <tbody>
                ${weeklyReportData.studentStats.map(student => `
                  <tr>
                    <td>${student.name}</td>
                    <td>${student.lessons}</td>
                    <td>${student.hours}</td>
                    <td>${student.courses.join(', ')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      // Create a blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${weeklyReportData.title.replace(/\s+/g, '_')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: "Report has been downloaded successfully.",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Error",
        description: "Failed to download report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Enhanced event click handler with loader
  const handleEventClickWithLoader = async (info: EventClickArg) => {
    setIsLoadingLessonDetails(true);
    try {
      await handleEventClick(info);
    } finally {
      setIsLoadingLessonDetails(false);
    }
  };

  // Handle event drop (drag and drop)
  const handleEventDrop = async (dropInfo: EventDropArg) => {
    const { event, delta, revert } = dropInfo;
    
    // Handle dragging of unavailable events
    if (event.extendedProps.isUnavailable) {
      try {
        const newStart = event.start!;
        const newEnd = event.end!;
        const newDate = format(newStart, 'yyyy-MM-dd');
        const newStartTime = format(newStart, 'HH:mm');
        const newEndTime = format(newEnd, 'HH:mm');

        // Update the unavailability in the database
        const db = getFirestore();
        const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
        const availabilityDoc = await getDoc(availabilityRef);
        
        if (!availabilityDoc.exists()) {
          revert();
          toast({
            title: "Error",
            description: "Availability data not found.",
            variant: "destructive",
          });
          return;
        }

        const currentSlots = availabilityDoc.data().unavailableSlots || [];
        
        // Find and update the specific unavailability slot
        const updatedSlots = currentSlots.map((slot: any) => {
          // Check if this is the slot we're updating
          const slotStart = new Date(slot.date + 'T' + slot.startTime);
          const slotEnd = new Date(slot.date + 'T' + slot.endTime);
          const eventStart = new Date(event.start!);
          const eventEnd = new Date(event.end!);
          
          // If this is the slot we're updating (within 1 minute tolerance)
          if (Math.abs(slotStart.getTime() - eventStart.getTime()) < 60000) {
            return {
              ...slot,
              date: newDate,
              startTime: newStartTime,
              endTime: newEndTime
            };
          }
          return slot;
        });

        // Update the database
        await setDoc(availabilityRef, {
          instructorEmail: currentUser?.email,
          unavailableSlots: updatedSlots,
          updatedAt: serverTimestamp()
        }, { merge: true });

        // Update local state
        setUnavailableSlots(updatedSlots);

        toast({
          title: "Success",
          description: "Unavailability period updated successfully.",
        });
      } catch (error) {
        console.error("Error updating unavailability:", error);
        revert();
        toast({
          title: "Error",
          description: "Failed to update unavailability period.",
          variant: "destructive",
        });
      }
      return;
    }

    // Don't allow dragging cancelled events
    if (event.extendedProps.status === 'cancelled') {
      revert();
      toast({
        title: "Cannot Move",
        description: "Cancelled lessons cannot be moved.",
        variant: "destructive",
      });
      return;
    }

    try {
      const newStart = event.start!;
      const newEnd = event.end!;
      const newDate = format(newStart, 'yyyy-MM-dd');
      const newStartTime = format(newStart, 'HH:mm');
      const newEndTime = format(newEnd, 'HH:mm');
      const duration = event.extendedProps.duration || 1;

      // Check if the new time is in the past
      if (newStart < new Date()) {
        if (!allowPastDateBooking) {
          revert();
          toast({
            title: "Invalid Time",
            description: "Cannot move lessons to the past. Enable 'Allow Past Date Booking' to record past lessons.",
            variant: "destructive",
          });
          return;
        } else {
          // Show warning for past date booking
          const shouldProceed = window.confirm(
            `You are moving a lesson to a past date (${format(newStart, 'EEEE, MMMM d, yyyy')} at ${newStartTime}). This is typically used to record lessons that were already conducted. Do you want to proceed?`
          );
          
          if (!shouldProceed) {
            revert();
            return;
          }
        }
      }

      // Check availability for the new time slot
      const isAvailable = await checkAvailability(newDate, newStartTime, duration, event.extendedProps.scheduleId);
      if (!isAvailable) {
        revert();
        toast({
          title: "Time Slot Unavailable",
          description: "The selected time slot is not available.",
          variant: "destructive",
        });
        return;
      }

      // Update the schedule in Firestore
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", event.extendedProps.scheduleId);
      
      await updateDoc(scheduleRef, {
        date: newDate,
        startTime: newStartTime,
        endTime: newEndTime,
        updatedAt: new Date().toISOString()
      });

      // Update local state immediately for better UX
      setEvents(prevEvents => 
        prevEvents.map(evt => 
          evt.id === event.id 
            ? {
                ...evt,
                start: newStart.toISOString(),
                end: newEnd.toISOString()
              }
            : evt
        )
      );

      // Refresh all data to ensure consistency
      await Promise.all([
        fetchSchedules(),
        fetchStudents()
      ]);

      toast({
        title: "Schedule Updated",
        description: `Lesson moved to ${format(newStart, 'EEEE, MMMM d, yyyy')} at ${newStartTime}`,
      });

    } catch (error) {
      console.error("Error updating schedule:", error);
      revert();
      toast({
        title: "Error",
        description: "Failed to update schedule. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Helper functions for overview period calculations
  const getDateRangeForPeriod = (date: Date, period: 'day' | 'week' | 'month') => {
    const start = new Date(date);
    const end = new Date(date);
    
    switch (period) {
      case 'day':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Start from Monday of the week
        const dayOfWeek = start.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(start.getDate() - daysToMonday);
        start.setHours(0, 0, 0, 0);
        // End on Sunday
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1, 0); // Last day of current month
        end.setHours(23, 59, 59, 999);
        break;
    }
    
    return { start, end };
  };

  const getEventsForPeriod = (period: 'day' | 'week' | 'month', date: Date) => {
    const { start, end } = getDateRangeForPeriod(date, period);
    
    return events.filter(event => {
      const eventDate = parseISO(event.start);
      return eventDate >= start && eventDate <= end && !event.extendedProps.isUnavailable;
    });
  };

  const getOverviewTitle = (period: 'day' | 'week' | 'month', date: Date) => {
    switch (period) {
      case 'day':
        return format(date, 'EEEE, MMMM d, yyyy');
      case 'week':
        const { start, end } = getDateRangeForPeriod(date, 'week');
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      case 'month':
        return format(date, 'MMMM yyyy');
      default:
        return format(date, 'EEEE, MMMM d, yyyy');
    }
  };

  const getOverviewStats = (period: 'day' | 'week' | 'month', date: Date) => {
    const periodEvents = getEventsForPeriod(period, date);
    
    const totalLessons = periodEvents.length;
    const totalHours = periodEvents.reduce((total, event) => total + (event.extendedProps.duration || 1), 0);
    const uniqueStudents = new Set(periodEvents.map(event => event.extendedProps.studentName)).size;
    const confirmedLessons = periodEvents.filter(event => event.extendedProps.status === 'confirmed').length;
    
    return {
      totalLessons,
      totalHours,
      uniqueStudents,
      confirmedLessons
    };
  };

  // Calculate current overview stats
  const currentStats = getOverviewStats(overviewPeriod, selectedOverviewDate);
  const currentPeriodEvents = getEventsForPeriod(overviewPeriod, selectedOverviewDate);

  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const [selectedUnavailableEvent, setSelectedUnavailableEvent] = useState<CalendarEvent | null>(null);

  // Add a state to track if booking is from override
  const [isBookingFromOverride, setIsBookingFromOverride] = useState(false);

  // Helper to calculate duration in hours from startTime and endTime strings
  const getOverrideDuration = () => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    const start = startHour + startMinute / 60;
    const end = endHour + endMinute / 60;
    return Math.max(0.25, end - start); // minimum 15 min
  };

  // Helper to get allowed durations for override slot
  const getOverrideDurations = () => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    const start = startHour + startMinute / 60;
    const end = endHour + endMinute / 60;
    const maxDuration = end - start;
    const allowedDurations = [1, 1.5, 2, 2.5, 3].filter(d => d <= maxDuration && d > 0);
    // Always include the exact slot duration if not already present
    if (maxDuration > 0 && !allowedDurations.includes(maxDuration)) {
      allowedDurations.push(maxDuration);
    }
    return allowedDurations.sort((a, b) => a - b);
  };

  // Helper to get allowed start times for override slot
  const getOverrideStartTimes = () => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    const start = startHour * 60 + startMinute;
    const end = endHour * 60 + endMinute;
    const times = [];
    for (let m = start; m <= end - 15; m += 15) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      times.push(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }
    return times;
  };

  // Helper to get allowed durations for override slot based on selected start time
  const getOverrideDurationsForStart = (start: string) => {
    // Validate input
    if (!start || typeof start !== 'string') {
      return [1]; // Default fallback
    }
    
    try {
      const [slotEndHour, slotEndMinute] = endTime.split(':').map(Number);
      const slotEnd = slotEndHour * 60 + slotEndMinute;
      const [selectedStartHour, selectedStartMinute] = start.split(':').map(Number);
      const selectedStart = selectedStartHour * 60 + selectedStartMinute;
      const maxDuration = (slotEnd - selectedStart) / 60;
      const allowedDurations = [1, 1.5, 2, 2.5, 3].filter(d => d <= maxDuration && d > 0);
      // Always include the exact slot duration if not already present
      if (maxDuration > 0 && !allowedDurations.includes(maxDuration)) {
        allowedDurations.push(maxDuration);
      }
      return allowedDurations.sort((a, b) => a - b);
    } catch (error) {
      console.error('Error calculating override durations:', error);
      return [1]; // Default fallback
    }
  };

  // Add function to handle reschedule
  const handleReschedule = async () => {
    if (!selectedEvent?.extendedProps.scheduleId) {
      toast({
        title: "Error",
        description: "Schedule ID not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsRescheduling(true);
    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", selectedEvent.extendedProps.scheduleId);
      
      // Check if the new time is in the past
      const rescheduleDateTime = new Date(`${rescheduleData.date}T${rescheduleData.startTime}`);
      const now = new Date();
      
      if (rescheduleDateTime < now) {
        if (!allowPastDateBooking) {
          toast({
            title: "Past Date Not Allowed",
            description: "Cannot reschedule lessons for past dates. Enable 'Allow Past Date Booking' to record past lessons.",
            variant: "destructive",
          });
          setIsRescheduling(false);
          return;
        } else {
          // Show warning for past date booking
          const shouldProceed = window.confirm(
            `You are rescheduling a lesson for a past date (${format(rescheduleDateTime, 'EEEE, MMMM d, yyyy')} at ${rescheduleData.startTime}). This is typically used to record lessons that were already conducted. Do you want to proceed?`
          );
          
          if (!shouldProceed) {
            setIsRescheduling(false);
            return;
          }
        }
      }

      // Check availability for the new time slot
      const isAvailable = await checkAvailability(
        rescheduleData.date,
        rescheduleData.startTime,
        parseInt(rescheduleData.duration),
        selectedEvent.extendedProps.scheduleId
      );
      
      if (!isAvailable) {
        toast({
          title: "Time Slot Unavailable",
          description: "The selected time slot is not available.",
          variant: "destructive",
        });
        setIsRescheduling(false);
        return;
      }

      // Calculate end time
      const duration = parseInt(rescheduleData.duration);
      const startTime = rescheduleData.startTime;
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const startMinutesTotal = startHours * 60 + startMinutes;
      const endMinutesTotal = startMinutesTotal + (duration * 60);
      const endHours = Math.floor(endMinutesTotal / 60);
      const endMinutes = endMinutesTotal % 60;
      const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

      // Update the schedule in Firestore
      await updateDoc(scheduleRef, {
        date: rescheduleData.date,
        startTime: startTime,
        endTime: endTime,
        startMinutes: startMinutesTotal,
        endMinutes: endMinutesTotal,
        duration: duration,
        venue: rescheduleData.venue || selectedEvent.extendedProps.venue,
        notes: rescheduleData.notes || selectedEvent.extendedProps.notes,
        updatedAt: new Date().toISOString()
      });

      toast({
        title: "Success",
        description: "Lesson rescheduled successfully!",
      });

      // Close dialogs and reset state
      setIsRescheduleDialogOpen(false);
      setIsDialogOpen(false);
      setSelectedEvent(null);
      setRescheduleData({
        date: "",
        startTime: "",
        duration: "1",
        venue: "",
        notes: "",
      });

      // Refresh the calendar
      await fetchSchedules();

    } catch (error) {
      console.error("Error rescheduling lesson:", error);
      toast({
        title: "Error",
        description: "Failed to reschedule lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  // Add function to open reschedule dialog
  const openRescheduleDialog = () => {
    if (!selectedEvent) return;
    
    const eventDate = new Date(selectedEvent.start);
    setRescheduleData({
      date: format(eventDate, 'yyyy-MM-dd'),
      startTime: format(eventDate, 'HH:mm'),
      duration: selectedEvent.extendedProps.duration?.toString() || "1",
      venue: selectedEvent.extendedProps.venue || "",
      notes: selectedEvent.extendedProps.notes || "",
    });
    setIsRescheduleDialogOpen(true);
  };

  // Add useEffect to generate time slots for reschedule
  useEffect(() => {
    const fetchRescheduleTimeSlots = async () => {
      if (isRescheduleDialogOpen && rescheduleData.date) {
        const slots = await generateTimeSlots(rescheduleData.date);
        setRescheduleAvailableTimeSlots(slots);
      }
    };
    
    fetchRescheduleTimeSlots();
  }, [isRescheduleDialogOpen, rescheduleData.date]);

  if (loading && isInitialLoad) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-[600px]">
          <p>Loading calendar...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b bg-gray-50/50 p-6 flex-none">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">My Schedule</h2>
            {isMobile && (
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('calendar')}
                >
                  <Calendar className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-6">
            {courses.map((course) => {
              const colors = courseColors[course.title] || courseColors.default;
              return (
                <div key={course.id} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ 
                      backgroundColor: colors.backgroundColor,
                      border: `2px solid ${colors.borderColor}`
                    }} 
                  />
                  <span className="text-sm font-medium">{course.title}</span>
                </div>
              );
            })}
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ 
                  backgroundColor: '#9C27B0',
                  border: '2px solid #7B1FA2'
                }} 
              />
              <span className="text-sm font-medium">Combined Driving Lesson</span>
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ 
                  backgroundColor: '#4B5563',
                  border: '2px solid #374151'
                }} 
              />
              <span className="text-sm font-medium text-gray-600">Unavailable (Full Day)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Overview Section */}
      <div className="border-b bg-white p-6 flex-none">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-drivewise-600" />
                Overview - {getOverviewTitle(overviewPeriod, selectedOverviewDate)}
              </h3>
              
              {/* Period Selector */}
              <div className="flex items-center gap-2">
                <Button
                  variant={overviewPeriod === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOverviewPeriod('day')}
                >
                  Day
                </Button>
                <Button
                  variant={overviewPeriod === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOverviewPeriod('week')}
                >
                  Week
                </Button>
                <Button
                  variant={overviewPeriod === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOverviewPeriod('month')}
                >
                  Month
                </Button>
              </div>
            </div>
            
            {/* Navigation Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(selectedOverviewDate);
                  switch (overviewPeriod) {
                    case 'day':
                      newDate.setDate(newDate.getDate() - 1);
                      break;
                    case 'week':
                      newDate.setDate(newDate.getDate() - 7);
                      break;
                    case 'month':
                      newDate.setMonth(newDate.getMonth() - 1);
                      break;
                  }
                  setSelectedOverviewDate(newDate);
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedOverviewDate(new Date())}
              >
                Today
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(selectedOverviewDate);
                  switch (overviewPeriod) {
                    case 'day':
                      newDate.setDate(newDate.getDate() + 1);
                      break;
                    case 'week':
                      newDate.setDate(newDate.getDate() + 7);
                      break;
                    case 'month':
                      newDate.setMonth(newDate.getMonth() + 1);
                      break;
                  }
                  setSelectedOverviewDate(newDate);
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Overview Statistics */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Total Lessons</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {currentStats.totalLessons}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">Confirmed</p>
                    <p className="text-2xl font-bold text-green-900">
                      {currentStats.confirmedLessons}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-700">Unique Students</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {currentStats.uniqueStudents}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-orange-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Total Hours</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {currentStats.totalHours}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-purple-200 rounded-full flex items-center justify-center">
                    <BarChart className="h-6 w-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Lessons List */}
          <div className="space-y-4">
            <div 
              className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
              onClick={() => setIsTodayLessonsCollapsed(!isTodayLessonsCollapsed)}
            >
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <List className="h-5 w-5 text-drivewise-600" />
                {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}'s Lessons
                <span className="text-sm font-normal text-gray-500">
                  ({currentPeriodEvents.length} lessons)
                </span>
              </h4>
              {isTodayLessonsCollapsed ? (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              )}
            </div>
            
            {!isTodayLessonsCollapsed && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(() => {
                  const periodEvents = currentPeriodEvents
                    .sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

                  if (periodEvents.length === 0) {
                    return (
                      <Card className="col-span-full">
                        <CardContent className="p-6 text-center">
                          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h5 className="text-lg font-medium text-gray-900 mb-2">
                            No Lessons {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}
                          </h5>
                          <p className="text-gray-500">
                            {overviewPeriod === 'day' ? 'You have a free day! Enjoy your time off.' : 
                             overviewPeriod === 'week' ? 'No lessons scheduled for this week.' : 
                             'No lessons scheduled for this month.'}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  }

                  return periodEvents.map((event, index) => (
                    <Card 
                      key={event.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer border-l-4"
                      style={{ borderLeftColor: event.backgroundColor }}
                      onClick={() => handleEventClick(createEventClickArg(event))}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-semibold text-gray-900">
                                {event.extendedProps.studentName}
                              </h5>
                              <Badge 
                                variant={
                                  event.extendedProps.status === 'confirmed' ? 'default' :
                                  event.extendedProps.status === 'pending' ? 'secondary' :
                                  event.extendedProps.status === 'completed' ? 'outline' : 'destructive'
                                }
                                className="text-xs"
                              >
                                {event.extendedProps.status?.charAt(0).toUpperCase() + event.extendedProps.status?.slice(1)}
                              </Badge>
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{format(parseISO(event.start), 'EEEE, MMMM d, yyyy')}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>
                                  {format(parseISO(event.start), 'HH:mm')} - {format(parseISO(event.end), 'HH:mm')}
                                  <span className="text-gray-500 ml-1">
                                    ({event.extendedProps.duration || 1} hour{event.extendedProps.duration !== 1 ? 's' : ''})
                                  </span>
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <BookOpen className="h-4 w-4" />
                                <span>{event.extendedProps.courseName}</span>
                              </div>
                              
                              {event.extendedProps.venue && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4" />
                                  <span>{event.extendedProps.venue}</span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span className="text-xs">
                                  Progress: {event.extendedProps.hoursTaken || 0}/{event.extendedProps.totalHours || 0} hours
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: event.backgroundColor }}
                            />
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2"
              onClick={() => setIsUnavailabilityDialogOpen(true)}
            >
              <Calendar className="h-4 w-4" />
              Set Unavailability
            </Button>
            
            {/* Past Date Booking Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-50">
              <Switch
                checked={allowPastDateBooking}
                onCheckedChange={setAllowPastDateBooking}
                id="past-date-booking"
              />
              <Label htmlFor="past-date-booking" className="text-sm font-medium">
                Allow Past Date Booking
              </Label>
            </div>
            
            {/* Report Type Selector */}
            <div className="flex items-center gap-2">
              <Select value={reportType} onValueChange={(value: 'daily' | 'weekly' | 'monthly') => setReportType(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2"
                onClick={() => generateReport(reportType)}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <BarChart className="h-4 w-4" />
                )}
                {isGeneratingReport ? 'Generating...' : `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 relative">
        {isMobile && viewMode === 'list' ? (
          <ScrollArea className="absolute inset-0">
            <div className="space-y-4 p-4">
              {getEventsByDate().map(([date, dayEvents]) => (
                <div key={date} className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                  </h3>
                  <div className="space-y-2">
                    {dayEvents.map((event) => (
                      <div
                        key={event.id}
                        className="bg-white rounded-lg border p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleListEventClick(event)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: event.backgroundColor }}
                              />
                              <h4 className="font-medium text-gray-900">
                                {event.extendedProps.studentName}
                              </h4>
                            </div>
                            <p className="text-sm text-gray-500">
                              {format(parseISO(event.start), 'HH:mm')} - {format(parseISO(event.end), 'HH:mm')}
                            </p>
                            <p className="text-sm text-gray-600">
                              {event.extendedProps.courseName}
                            </p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                        {event.extendedProps.venue && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <MapPin className="h-4 w-4 mr-1" />
                            {event.extendedProps.venue}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="absolute inset-0">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, scrollGridPlugin]}
              initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'timeGridWeek,timeGrid,timeGridDay'
              }}
              height="100%"
              contentHeight="auto"
              // Remove the license key
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              allDaySlot={false}
              aspectRatio={isMobile ? 1 : 1.5}
              handleWindowResize={true}
              windowResizeDelay={100}
              timeZone="local"
              stickyHeaderDates={true}
              slotLaneClassNames={(arg) => {
                const hour = new Date(arg.date).getHours();
                if (hour >= 22 || hour <= 7) {
                  return ['dark-grey-slot'];
                }
                return [];
              }}
              views={{
                timeGridWeek: {
                  type: 'timeGrid',
                  duration: { weeks: 1 },
                  buttonText: 'Week',
                  slotLabelFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }
                },
                timeGrid: {
                  type: 'timeGrid',
                  duration: { days: 3 },
                  buttonText: '3 Day',
                  slotLabelFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }
                },
                timeGridDay: {
                  type: 'timeGrid',
                  duration: { days: 1 },
                  buttonText: 'Day',
                  slotLabelFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }
                }
              }}
              eventContent={eventInfo => {
                const startTime = new Date(eventInfo.event.start!).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const endTime = new Date(eventInfo.event.end!).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                
                if (eventInfo.event.extendedProps.isUnavailable) {
                  return (
                    <div className={`p-2 overflow-hidden ${eventInfo.event.extendedProps.isFullDay ? 'bg-gray-600 text-white border-gray-700' : 'bg-red-50 border border-red-200'} rounded-md`}>
                      <div className={`font-semibold text-sm ${eventInfo.event.extendedProps.isFullDay ? 'text-white' : 'text-red-700'}`}>
                        {eventInfo.event.extendedProps.isFullDay ? 'Unavailable (Full Day)' : 'Unavailable'}
                      </div>
                      {!eventInfo.event.extendedProps.isFullDay && (
                        <div className="text-xs text-red-600">
                          {startTime} - {endTime}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div className="p-2 overflow-hidden">
                    <div className="font-semibold text-sm truncate text-white">
                      {eventInfo.event.extendedProps.studentName}
                    </div>
                    <div className="text-xs font-medium text-white opacity-90 truncate">
                      {startTime} - {endTime}
                    </div>
                  </div>
                );
              }}
              dayHeaderFormat={{ weekday: 'short' } as any}
              stickyFooterScrollbar={true}
              nowIndicator={true}
              scrollTimeReset={false}
              expandRows={true}
              slotEventOverlap={false}
              eventMaxStack={3}
              eventMinHeight={25}
              eventShortHeight={30}
              eventLongPressDelay={200}
              eventDisplay="block"
              eventClassNames={(arg) => {
                const baseClasses = "hover:opacity-90 cursor-pointer rounded-md border";
                if (arg.event.extendedProps.isUnavailable) {
                  return `${baseClasses} cursor-move opacity-80`;
                }
                if (arg.event.extendedProps.status === 'cancelled') {
                  return `${baseClasses} cursor-not-allowed opacity-40`;
                }
                return `${baseClasses} cursor-move`;
              }}
              editable={true}
              eventConstraint={{
                startTime: '08:00',
                endTime: '20:00',
                dows: [1, 2, 3, 4, 5] // Monday to Friday
              }}
              selectable={true}
              selectMirror={true}
              select={handleDateSelect}
              events={events}
              eventClick={handleEventClickWithLoader}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              eventDrop={handleEventDrop}
            />
          </div>
        )}
      </div>

      {/* Student Schedule Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6 border">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl md:text-2xl">Student Schedule Details</DialogTitle>
            <DialogDescription className="text-sm md:text-base">
              View detailed information about the student and their lesson schedule.
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingStudentDetails ? (
            <div className="flex items-center justify-center py-8">
              <p>Loading student details...</p>
            </div>
          ) : selectedEvent ? (
            <div className="space-y-6">
              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-drivewise-600" />
                  Student Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {selectedEvent.extendedProps.studentName}</p>
                    <p><span className="font-medium">Email:</span> {selectedEvent.extendedProps.studentEmail}</p>
                    <p><span className="font-medium">Phone:</span> {selectedEvent.extendedProps.studentPhone || 'Not provided'}</p>
                    <p><span className="font-medium">Address:</span> {selectedEvent.extendedProps.studentAddress || 'Not provided'}</p>
                    <p><span className="font-medium">Postcode:</span> {selectedEvent.extendedProps.studentPostcode || 'Not provided'}</p>
                  </div>
                  <div className="space-y-2">
                    <p><span className="font-medium">Gearbox:</span> {selectedEvent.extendedProps.gearbox || 'Not provided'}</p>
                    <p><span className="font-medium">License:</span> {selectedEvent.extendedProps.licenseNumber || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Schedule Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-drivewise-600" />
                  Schedule Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p><span className="font-medium">Date:</span> {format(new Date(selectedEvent.start), "EEEE, MMMM d, yyyy")}</p>
                    <p><span className="font-medium">Time:</span> {format(new Date(selectedEvent.start), "HH:mm")} - {format(new Date(selectedEvent.end), "HH:mm")}</p>
                    <p><span className="font-medium">Duration:</span> {selectedEvent.extendedProps.duration} hour(s)</p>
                    <p><span className="font-medium">Venue:</span> {selectedEvent.extendedProps.venue || 'Not specified'}</p>
                    </div>
                    </div>
                  </div>

              {/* Course Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-drivewise-600" />
                  Course Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p><span className="font-medium">Course Name:</span> {selectedEvent.extendedProps.courseName}</p>
                    <p><span className="font-medium">Course Type:</span> {selectedEvent.extendedProps.courseType}</p>
                    <p><span className="font-medium">Course Subtype:</span> {selectedEvent.extendedProps.courseSubtype}</p>
                    </div>
                  <div className="space-y-2">
                    <p><span className="font-medium">Vehicle Type:</span> {selectedEvent.extendedProps.vehicleType}</p>
                    <p><span className="font-medium">License Type:</span> {selectedEvent.extendedProps.licenseType}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Status:</span>
                      <Badge variant={selectedEvent.extendedProps.status === "confirmed" ? "default" : selectedEvent.extendedProps.status === "cancelled" ? "destructive" : "secondary"}>
                        {selectedEvent.extendedProps.status.charAt(0).toUpperCase() + selectedEvent.extendedProps.status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Progress */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-drivewise-600" />
                  Course Progress
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p><span className="font-medium">Hours Taken:</span> {selectedEvent.extendedProps.hoursTaken} hours</p>
                    <p><span className="font-medium">Total Hours:</span> {selectedEvent.extendedProps.totalHours} hours</p>
                    <p><span className="font-medium">Progress:</span> {selectedEvent.extendedProps.hoursTaken} of {selectedEvent.extendedProps.totalHours} hours completed</p>
            </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-drivewise-600 h-2.5 rounded-full" 
                      style={{ width: `${(selectedEvent.extendedProps.hoursTaken / selectedEvent.extendedProps.totalHours) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Student Progress */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-drivewise-600" />
                  Student Progress
                </h3>
                <div className="space-y-2">
                  <p><span className="font-medium">Completed Lessons:</span> {selectedEvent.extendedProps.completedLessons || 0}</p>
                  <p><span className="font-medium">Total Lessons:</span> {selectedEvent.extendedProps.totalLessons || 0}</p>
                  <p><span className="font-medium">Last Lesson:</span> {selectedEvent.extendedProps.lastLessonDate ? format(new Date(selectedEvent.extendedProps.lastLessonDate), "MMMM d, yyyy") : 'Not available'}</p>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedEvent.extendedProps.status && selectedEvent.extendedProps.status !== 'cancelled' && (
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={openRescheduleDialog}
                  >
                    Reschedule Lesson
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleCancelSchedule}
                    disabled={isCancelling}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Lesson'
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p>No student details available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unavailability Dialog */}
      <Dialog open={isUnavailabilityDialogOpen} onOpenChange={setIsUnavailabilityDialogOpen}>
        <DialogContent className="sm:max-w-[600px] border">
          <DialogHeader>
            <DialogTitle>Set Unavailability</DialogTitle>
            <DialogDescription>
              Set your unavailability for {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="fullDay"
                checked={isFullDay}
                onCheckedChange={(checked) => {
                  setIsFullDay(checked as boolean);
                  if (checked) {
                    setStartTime('00:00');
                    setEndTime('23:59');
                  }
                }}
              />
              <label htmlFor="fullDay" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Full Day Unavailable
              </label>
            </div>
            {!isFullDay && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startTime">Start Time</Label>
                    <Input
                      id="startTime"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endTime">End Time</Label>
                    <Input
                      id="endTime"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="repeat">Repeat</Label>
              <Select
                value={repeatSettings.type}
                onValueChange={(value: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly') => 
                  setRepeatSettings(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select repeat option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Repeat</SelectItem>
                  <SelectItem value="daily">Every Day</SelectItem>
                  <SelectItem value="weekly">Every Week</SelectItem>
                  <SelectItem value="biweekly">Every Two Weeks</SelectItem>
                  <SelectItem value="monthly">Every Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {repeatSettings.type !== 'none' && (
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={repeatSettings.endDate ? format(repeatSettings.endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setRepeatSettings(prev => ({
                    ...prev,
                    endDate: e.target.value ? new Date(e.target.value) : undefined
                  }))}
                  min={format(selectedDate || new Date(), 'yyyy-MM-dd')}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUnavailabilityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetUnavailability} disabled={isSubmitting}>
              {isSubmitting ? 'Setting...' : 'Set Unavailability'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Booking Dialog */}
      <Dialog open={isBookingDialogOpen} onOpenChange={(open) => {
        setIsBookingDialogOpen(open);
        if (!open) {
          // Reset all booking-related state when dialog closes
          setIsBookingFromOverride(false);
          setSelectedStudent(null);
          setSelectedEnrollment(null);
          setBookingData({
            date: "",
            startTime: "",
            duration: "1",
            venue: "",
            notes: "",
          });
        } else {
          // Reset state when dialog opens to ensure fresh start
          setSelectedStudent(null);
          setSelectedEnrollment(null);
          setBookingData({
            date: format(selectedDateForBooking || new Date(), 'yyyy-MM-dd'),
            startTime: startTime || "",
            duration: "1",
            venue: "",
            notes: "",
          });
        }
      }}>
        <DialogContent className="sm:max-w-[600px] border">
          <DialogHeader>
            <DialogTitle>Book a Lesson</DialogTitle>
            <DialogDescription>
              Select a student and course to book a lesson for {selectedDateForBooking ? format(selectedDateForBooking, 'MMMM d, yyyy') : ''}
            </DialogDescription>
            {/* Past Date Warning */}
            {selectedDateForBooking && selectedDateForBooking < new Date() && (
              <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    Past Date Selected
                  </span>
                </div>
                <p className="text-xs text-orange-700 mt-1">
                  You're booking a lesson for a past date. This is typically used to record lessons that were already conducted.
                  {!allowPastDateBooking && " Enable 'Allow Past Date Booking' to proceed."}
                </p>
              </div>
            )}
          </DialogHeader>
          {isLoadingBooking ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-drivewise-600" />
              <p className="text-sm text-gray-500">Loading students and courses...</p>
            </div>
          ) : (
            <div className="grid gap-4 py-4">
              {!selectedStudent && (
                <div className="space-y-2">
                  <Label>Select Student</Label>
                  <Select
                    onValueChange={(value) => {
                      const student = students.find(s => s.originalId === value);
                      setSelectedStudent(student || null);
                      setSelectedEnrollment(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.originalId}>
                          {student.firstName} {student.lastName} ({student.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedStudent && !selectedEnrollment && (
                <div className="space-y-2">
                  <Label>Select Course</Label>
                  <Select
                    onValueChange={(value) => {
                      const enrollment = selectedStudent.enrollments.find(e => e.id === value);
                      setSelectedEnrollment(enrollment || null);
                      if (enrollment) {
                        setBookingData(prev => ({
                          ...prev,
                          date: format(selectedDateForBooking || new Date(), "yyyy-MM-dd")
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedStudent.enrollments.map((enrollment) => (
                        <SelectItem key={`enrollment-${enrollment.id}`} value={enrollment.id}>
                          {enrollment.courseName} ({enrollment.hoursTaken}/{enrollment.totalHours} hours)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedEnrollment && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Start Time</Label>
                      <Select
                        value={bookingData.startTime}
                        onValueChange={(value) => {
                          setBookingData((prev) => {
                            // If the current duration is not valid for the new start time, reset to max allowed
                            const allowed = getOverrideDurationsForStart(value);
                            let duration = prev.duration;
                            if (!allowed.includes(Number(duration))) {
                              duration = allowed[allowed.length - 1].toString();
                            }
                            return { ...prev, startTime: value, duration };
                          });
                        }}
                        // Remove disabled for override, allow selection
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {isBookingFromOverride ? (
                            getOverrideStartTimes().map((time) => (
                              <SelectItem key={`time-${time}`} value={time}>
                                {time}
                              </SelectItem>
                            ))
                          ) : (
                            availableTimeSlots.map((time) => (
                              <SelectItem key={`time-${time}`} value={time}>
                                {time}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration (hours)</Label>
                      <Select
                        value={bookingData.duration}
                        onValueChange={(value) => setBookingData({ ...bookingData, duration: value })}
                        // Only restrict durations if booking from override
                        disabled={false}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          {isBookingFromOverride ? (
                            getOverrideDurationsForStart(bookingData.startTime || startTime || '09:00').map((duration) => (
                              <SelectItem key={`duration-${duration}`} value={duration.toString()}>
                                {duration} {duration === 1 ? "hour" : "hours"}
                              </SelectItem>
                            ))
                          ) : (
                            [1, 1.5, 2, 2.5, 3].map((duration) => (
                              <SelectItem key={`duration-${duration}`} value={duration.toString()}>
                                {duration} {duration === 1 ? "hour" : "hours"}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="venue">Venue</Label>
                    <Input
                      id="venue"
                      value={bookingData.venue}
                      onChange={(e) => setBookingData({ ...bookingData, venue: e.target.value })}
                      placeholder="Enter meeting location"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Input
                      id="notes"
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                      placeholder="Any special instructions or notes"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              // Explicitly reset all state when canceling
              setSelectedStudent(null);
              setSelectedEnrollment(null);
              setBookingData({
                date: "",
                startTime: "",
                duration: "1",
                venue: "",
                notes: "",
              });
              setIsBookingFromOverride(false);
              setIsBookingDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBooking} 
              disabled={!selectedStudent || !selectedEnrollment || !bookingData.startTime || isLoadingBooking}
            >
              {isLoadingBooking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Booking...
                </>
              ) : (
                'Book Lesson'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly Report Dialog */}
      <Dialog open={isWeeklyReportOpen} onOpenChange={setIsWeeklyReportOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {weeklyReportData?.title || 'Report'}
                </DialogTitle>
                <DialogDescription>
                  {weeklyReportData && (
                    <span>
                      {weeklyReportData.startDate} - {weeklyReportData.endDate} • Generated on {weeklyReportData.generatedAt}
                    </span>
                  )}
                </DialogDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2"
                onClick={downloadPDF}
                disabled={isDownloadingPDF}
              >
                {isDownloadingPDF ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isDownloadingPDF ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </DialogHeader>
          
          {weeklyReportData && (
            <div className="space-y-6">
              {/* Period Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-blue-700">Total Lessons</p>
                      <p className="text-2xl font-bold text-blue-900">{weeklyReportData.periodTotals.totalLessons}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-green-700">Total Hours</p>
                      <p className="text-2xl font-bold text-green-900">{weeklyReportData.periodTotals.totalHours}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-purple-700">Unique Students</p>
                      <p className="text-2xl font-bold text-purple-900">{weeklyReportData.periodTotals.uniqueStudents}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-orange-700">Completed</p>
                      <p className="text-2xl font-bold text-orange-900">{weeklyReportData.periodTotals.completedLessons}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Breakdown */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Daily Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {weeklyReportData.dailyBreakdown.map((day) => (
                    <Card key={day.date} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">{day.dayName}</CardTitle>
                        <p className="text-sm text-gray-500">{format(parseISO(day.date), 'MMMM d, yyyy')}</p>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Lessons:</span>
                            <span className="font-medium">{day.totalLessons}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Hours:</span>
                            <span className="font-medium">{day.totalHours}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Confirmed:</span>
                            <span className="font-medium text-green-600">{day.confirmedLessons}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Pending:</span>
                            <span className="font-medium text-yellow-600">{day.pendingLessons}</span>
                          </div>
                        </div>
                        
                        {day.events.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <h4 className="text-sm font-medium">Lessons:</h4>
                            {day.events.map((event, index) => (
                              <div 
                                key={index}
                                className="p-2 bg-gray-50 rounded text-sm cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => handleEventClickWithLoader(createEventClickArg(event))}
                              >
                                <div className="font-medium">{event.extendedProps.studentName}</div>
                                <div className="text-gray-600">
                                  {format(parseISO(event.start), 'HH:mm')} - {format(parseISO(event.end), 'HH:mm')}
                                </div>
                                <div className="text-gray-500 text-xs">{event.extendedProps.courseName}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Student Statistics */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Student Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {weeklyReportData.studentStats.map((student, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">{student.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Lessons:</span>
                            <span className="font-medium">{student.lessons}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Hours:</span>
                            <span className="font-medium">{student.hours}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600">Courses:</span>
                            <div className="mt-1">
                              {student.courses.map((course, courseIndex) => (
                                <span 
                                  key={courseIndex}
                                  className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded mr-1 mb-1"
                                >
                                  {course}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Loading Overlay for Lesson Details */}
      {isLoadingLessonDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-drivewise-600" />
            <p className="text-lg font-medium">Loading lesson details...</p>
          </div>
        </div>
      )}

      {/* Override Dialog */}
      <Dialog open={isOverrideDialogOpen} onOpenChange={setIsOverrideDialogOpen}>
        <DialogContent className="sm:max-w-[500px] border">
          <DialogHeader>
            <DialogTitle>Slot Unavailable</DialogTitle>
            <DialogDescription>
              This slot is currently marked as unavailable. You can override and book for a student, or remove the unavailability.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="text-sm text-gray-700">
              <div><b>Date:</b> {selectedUnavailableEvent ? format(new Date(selectedUnavailableEvent.start), 'EEEE, MMMM d, yyyy') : ''}</div>
              <div><b>Time:</b> {selectedUnavailableEvent ? `${format(new Date(selectedUnavailableEvent.start), 'HH:mm')} - ${format(new Date(selectedUnavailableEvent.end), 'HH:mm')}` : ''}</div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => {
                  // Pre-fill booking dialog with slot info
                  if (selectedUnavailableEvent) {
                    setIsOverrideDialogOpen(false);
                    setSelectedDate(new Date(selectedUnavailableEvent.start));
                    setSelectedDateForBooking(new Date(selectedUnavailableEvent.start));
                    setStartTime(format(new Date(selectedUnavailableEvent.start), 'HH:mm'));
                    setEndTime(format(new Date(selectedUnavailableEvent.end), 'HH:mm'));
                    setStudents([]); // Reset students
                    setSelectedStudent(null); // Reset selected student
                    setSelectedEnrollment(null); // Reset selected enrollment
                    setIsBookingFromOverride(true); // Mark as override
                    // Pre-fill bookingData with slot start and max duration
                    const slotStart = format(new Date(selectedUnavailableEvent.start), 'HH:mm');
                    const [slotStartHour, slotStartMinute] = slotStart.split(':').map(Number);
                    const slotEnd = format(new Date(selectedUnavailableEvent.end), 'HH:mm');
                    const [slotEndHour, slotEndMinute] = slotEnd.split(':').map(Number);
                    const maxDuration = ((slotEndHour * 60 + slotEndMinute) - (slotStartHour * 60 + slotStartMinute)) / 60;
                    setBookingData(prev => ({
                      ...prev,
                      startTime: slotStart,
                      duration: maxDuration.toString(),
                      date: format(new Date(selectedUnavailableEvent.start), 'yyyy-MM-dd'),
                    }));
                    setIsLoadingBooking(true); // Show loading spinner
                    setIsBookingDialogOpen(true);
                    setTimeout(() => fetchStudents().finally(() => setIsLoadingBooking(false)), 0);
                  }
                }}
                className="w-full"
              >
                Override and Book for Student
              </Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  if (selectedUnavailableEvent) {
                    setIsOverrideDialogOpen(false);
                    await handleDeleteUnavailability(selectedUnavailableEvent);
                  }
                }}
                className="w-full"
              >
                Remove Unavailability
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsOverrideDialogOpen(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-[600px] border">
          <DialogHeader>
            <DialogTitle>Reschedule Lesson</DialogTitle>
            <DialogDescription>
              Select a new time and date for the lesson.
            </DialogDescription>
          </DialogHeader>
          {isRescheduling ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-drivewise-600" />
              <p className="text-sm text-gray-500">Rescheduling lesson...</p>
            </div>
          ) : (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                  min={format(selectedDate || new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={rescheduleData.startTime}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, startTime: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (hours)</Label>
                <Select
                  value={rescheduleData.duration}
                  onValueChange={(value) => setRescheduleData({ ...rescheduleData, duration: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {isBookingFromOverride ? (
                      getOverrideDurationsForStart(rescheduleData.startTime || startTime || '09:00').map((duration) => (
                        <SelectItem key={`duration-${duration}`} value={duration.toString()}>
                          {duration} {duration === 1 ? "hour" : "hours"}
                        </SelectItem>
                      ))
                    ) : (
                      [1, 1.5, 2, 2.5, 3].map((duration) => (
                        <SelectItem key={`duration-${duration}`} value={duration.toString()}>
                          {duration} {duration === 1 ? "hour" : "hours"}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue">Venue</Label>
                <Input
                  id="venue"
                  value={rescheduleData.venue}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, venue: e.target.value })}
                  placeholder="Enter meeting location"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Input
                  id="notes"
                  value={rescheduleData.notes}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, notes: e.target.value })}
                  placeholder="Any special instructions or notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRescheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={isRescheduling}>
              {isRescheduling ? 'Rescheduling...' : 'Reschedule Lesson'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstructorCalendar; 